/**
 * OSM-derived building footprints extruded into sandstone-cottage massing
 * along the fly-through route (docs/plans/2026-07-02-osm-buildings-design.md).
 * Purely illustrative scene furniture — real footprints give a sense of the
 * settlements passed (Ridsdale, Woodburn, Corsenside), but this has NO
 * bearing on any LOS result (hard constraint #1: LOS truth is raypath's
 * analytical raster) and no elevation here is ever invented: every
 * building's base comes from the same collider `sampleGround` that backs
 * walk-mode ground lock (hard constraint #6).
 *
 * The pure geometry-building functions below (footprintToLocal,
 * buildingHeight, ridgeAxis, buildWalls, buildRoof, chimneys) are
 * three.js-free and unit-tested directly in Node. BuildingsLayer wraps them
 * in a three.js Group and exposes update(sampleGround) so buildings can be
 * draped incrementally as terrain tiles stream in — mirrors RouteRibbon
 * (viewer/src/terrain/routeRibbon.ts).
 */
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
} from 'three';
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import { buildingFillPremultiplied } from './lighting';
import type { Building } from '../core/manifest';
import type { GroundSampler } from '../camera/groundLock';
import {
  makeSandstoneTexture,
  makeFacadeTexture,
  makeRoofTexture,
  isWallMaterialFamily,
  wallColorBucket,
  isValidHexColor,
  parseHexColor,
  nearestPaletteIndex,
  facadeGrid,
  doorSlot,
  doorColorBucket,
  wallBaseColor,
  wallTintColor,
  clampWallTint,
  DEFAULT_WALL_COLOR_BUCKET,
  WALL_COLOR_BUCKET_COUNT,
  DOOR_TINT_PALETTE,
  DEFAULT_DOOR_TINT_RGB,
  SANDSTONE_BASE_RGB,
  FRAME_RGB,
  GLAZING_RGB,
  WINDOW_W,
  WINDOW_H,
  type WallMaterialFamily,
  type RgbColor,
} from './facadeTexture';
import { decomposeFootprint, isSliverLobe, polygonArea } from './roofLobes';
import {
  isTerraceCandidate,
  bayCount,
  segmentFootprint,
  steppedEaves,
  reconcileDwellings,
  type BayDwelling,
} from './terraceSegments';
import { assembleParts, buildTowerCap, buildPorchMass, type PartsContext } from './buildingParts';
import {
  parseFacades,
  matchFacade,
  wallBearingDeg,
  buildObservedFacadeDetail,
  frameExpand,
  addSill,
  type ParsedFacade,
} from './facadeOverlay';
import { verifiedOpenBarnForms, type AppearanceGroundTruth, type OpenBarnForm } from './barnVerification';

export const STOREY_M = 2.6;
export const NOMINAL_LEVELS = 2;

/** Default roof pitch (degrees) shared by buildRoof and chimneys so the two
 * agree on the eaves-to-apex slope a chimney has to sit on. */
export const DEFAULT_ROOF_PITCH_DEG = 30;

/** Default eaves height (m) for a non-domestic building (barn/shed/etc, issue
 * #113) that has no explicit height_m/levels/usable storeys. Unlike the
 * domestic nominal default, this is never jittered -- every un-dimensioned
 * agricultural/industrial building reads at the same utilitarian scale. */
export const BARN_EAVES_M = 4.5;

/** Default roof pitch (degrees) for a non-domestic building (issue #113) --
 * shallower than DEFAULT_ROOF_PITCH_DEG, matching a typical shed/barn roof. */
export const BARN_ROOF_PITCH_DEG = 15;

/** Minimum classifier confidence for an observed `storeys` count to drive
 * extrusion height (issue #104). Below this the value is ignored outright and
 * the nominal-default path is used, never a clamped or blended value. */
export const STOREYS_HEIGHT_MIN_CONFIDENCE = 0.7;

// Fixed chimney box dimensions (metres) — every chimney uses the same
// dimensions so callers (BuildingsLayer) can share a single BoxGeometry
// instead of allocating one per chimney.
export const CHIMNEY_WIDTH_M = 0.6;
export const CHIMNEY_HEIGHT_M = 0.8;

/** Deterministic fractional hash in [0, 1) seeded by an integer (same idiom as turbines.ts). */
export function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Maps a BNG footprint ring (open — first point != last, see buildings.json
 * comment) to local viewer-space x/z via bngToLocal. Never hardcode the
 * sign of x/z here — always route through bngToLocal so a handedness fix
 * on another branch keeps buildings correct (design doc "Notes").
 */
export function footprintToLocal(ringBng: [number, number][]): LocalXZ[] {
  return ringBng.map(([e, n]) => bngToLocal(e, n));
}

/**
 * True when an observed Street View `storeys` count is trustworthy enough to
 * drive extrusion height (issue #104): the whole-appearance confidence must
 * clear STOREYS_HEIGHT_MIN_CONFIDENCE AND storeys must be an integer in 1..3.
 * A null / 0 / >3 / non-integer count, or a missing/low confidence, is treated
 * as absent — never clamped into range (predictable-failure-cases table).
 */
function usableStoreys(appearance: BuildingAppearance | undefined): number | null {
  if (!appearance) return null;
  const { storeys, confidence } = appearance;
  if (confidence == null || confidence < STOREYS_HEIGHT_MIN_CONFIDENCE) return null;
  if (storeys == null || !Number.isInteger(storeys) || storeys < 1 || storeys > 3) return null;
  return storeys;
}

/** A LiDAR-measured eaves/ridge pair pulled from `appearance.heights` (#119). */
export interface MeasuredHeights {
  eaves_m: number;
  ridge_m: number;
  tower: { top_m: number } | null;
}

/**
 * Reads a trustworthy measured height block from `appearance.heights` (the
 * `measured` top rung of the provenance ladder, LiDAR DSM - DTM; issue #119).
 * Returns null — so every consumer falls back to the existing height ladder —
 * unless the block is explicitly `provenance: "measured"` with finite eaves and
 * ridge where `ridge_m >= eaves_m > 0`. A mislabelled or malformed block is
 * never trusted and never clamped (predictable-failure-cases; hard constraint
 * #7). The `tower` sub-element is carried through but not rendered yet (that is
 * the parts batch, #114 follow-on).
 */
export function measuredHeights(appearance: BuildingAppearance | undefined): MeasuredHeights | null {
  const h = appearance?.heights;
  if (!h || h.provenance !== 'measured') return null;
  const { eaves_m, ridge_m } = h;
  if (typeof eaves_m !== 'number' || !Number.isFinite(eaves_m) || eaves_m <= 0) return null;
  if (typeof ridge_m !== 'number' || !Number.isFinite(ridge_m) || ridge_m < eaves_m) return null;
  const tower =
    h.tower && typeof h.tower.top_m === 'number' && Number.isFinite(h.tower.top_m)
      ? { top_m: h.tower.top_m }
      : null;
  return { eaves_m, ridge_m, tower };
}

/**
 * Roof pitch (degrees) that raises a gable apex from `eavesM` to the measured
 * `ridgeM` over the roof's cross-ridge half-span (#119). buildRoof derives each
 * lobe apex as `eaves + tan(pitch) * halfSpan`, so inverting that here lets a
 * measured ridge drive the apex instead of a nominal pitch. Returns 0 for a
 * flat measured roof or a degenerate half-span (never NaN/Infinity).
 */
export function roofPitchDegForRidge(eavesM: number, ridgeM: number, halfSpan: number): number {
  if (!(halfSpan > 0) || ridgeM <= eavesM) return 0;
  return (Math.atan((ridgeM - eavesM) / halfSpan) * 180) / Math.PI;
}

/** Domestic/non-domestic use-bucket a building is rendered under (issue #113). */
export type BuildingUseBucket = 'domestic' | 'non-domestic';

/** OSM `building=` values treated as non-domestic (agricultural/industrial
 * massing, issue #113) — checked before the classifier's `building_use`,
 * mirroring #104's explicit-OSM-first precedence. */
export const NON_DOMESTIC_OSM_BUILDING: ReadonlySet<string> = new Set([
  'barn',
  'cowshed',
  'stable',
  'sty',
  'farm_auxiliary',
  'shed',
  'industrial',
  'warehouse',
  'silo',
]);

/**
 * The traditionally timber-clad agricultural subset of
 * {@link NON_DOMESTIC_OSM_BUILDING} (issue #133; `shed` added by #137). With
 * no Street View appearance data these default to the wood-toned `timber`
 * family drawn with vertical plank cladding, rather than the
 * `metal`/corrugated-sheet default the other non-domestic types
 * (industrial/warehouse/silo) keep.
 */
export const AGRICULTURAL_TIMBER_OSM_BUILDING: ReadonlySet<string> = new Set([
  'barn',
  'cowshed',
  'stable',
  'sty',
  'farm_auxiliary',
  'shed',
]);

/**
 * No-appearance wall-material family for a non-domestic building (issue #133):
 * `timber` (vertical plank cladding) for the agricultural subset above,
 * `metal` (corrugated sheet, horizontal coursing) for everything else. Only the
 * fallback used when a building has no usable classified `wall_material` — a
 * building with valid Street View appearance data keeps rendering that data
 * (see resolveFacadeSelection), never this default.
 */
export function nonDomesticFallbackFamily(osmBuilding: string | undefined): WallMaterialFamily {
  return osmBuilding != null && AGRICULTURAL_TIMBER_OSM_BUILDING.has(osmBuilding) ? 'timber' : 'metal';
}

/**
 * No-appearance wall-colour bucket override for the `metal` fallback family
 * (issue #137). `industrial` buildings read as galvanized corrugated steel,
 * not brick, so they get the palette's zinc-grey bucket (index 2) instead of
 * the shared `DEFAULT_WALL_COLOR_BUCKET` (index 1, rust-brown) that every
 * other non-domestic no-appearance case keeps. This is a local override of
 * this one call site only — it does not change `DEFAULT_WALL_COLOR_BUCKET`
 * itself or any of its other uses (e.g. resolveFacadeSelection's
 * missing-colour fallback).
 */
export function nonDomesticFallbackBucket(osmBuilding: string | undefined): number {
  return osmBuilding === 'industrial' ? 2 : DEFAULT_WALL_COLOR_BUCKET;
}

/** OSM `building=` values treated as domestic (issue #113). `"farm"` denotes
 * the farmhouse dwelling itself (as opposed to `"farm_auxiliary"`, its
 * outbuildings), so it belongs here rather than in NON_DOMESTIC_OSM_BUILDING. */
export const DOMESTIC_OSM_BUILDING: ReadonlySet<string> = new Set([
  'house',
  'detached',
  'semidetached_house',
  'terrace',
  'cottage',
  'bungalow',
  'residential',
  'apartments',
  'farm',
]);

/**
 * Domestic/non-domestic use-bucket, first match wins (issue #113):
 *   1. explicit OSM `building=` tag, if it's in either recognised set;
 *   2. otherwise the classifier's `appearance.building_use` ("agricultural"
 *      -> non-domestic, "dwelling" -> domestic);
 *   3. otherwise domestic — today's unconditional behaviour, unchanged.
 * An osm_building value in neither set (e.g. "school", "yes"), or any
 * unrecognised building_use string, falls through to the next step rather
 * than throwing — never crashes on an unknown string. This includes the
 * classifier's mixed-use "other" bucket (pub/shop over a dwelling, issue
 * #155): it isn't "agricultural", so it falls through to the domestic
 * default — deliberately, since these buildings have windows like a dwelling.
 */
export function resolveBuildingUse(osmBuilding: string | undefined, buildingUse: string | undefined): BuildingUseBucket {
  if (osmBuilding != null) {
    if (NON_DOMESTIC_OSM_BUILDING.has(osmBuilding)) return 'non-domestic';
    if (DOMESTIC_OSM_BUILDING.has(osmBuilding)) return 'domestic';
  }
  if (buildingUse === 'agricultural') return 'non-domestic';
  if (buildingUse === 'dwelling') return 'domestic';
  return 'domestic';
}

/**
 * Eaves height (m) for a building. Precedence, first match wins:
 *   0. a LiDAR-measured appearance.heights.eaves_m (issue #119 — the `measured`
 *      top rung; beats even explicit OSM, being a real surveyed height)
 *   1. published height_m (explicit OSM)
 *   2. levels * STOREY_M (explicit OSM)
 *   3. a high-confidence observed appearance.storeys * STOREY_M (Street View,
 *      only where OSM has no explicit data — usableStoreys() gates it)
 *   4. a nominal NOMINAL_LEVELS-storey cottage with small deterministic
 *      per-building variation from hash01(seedIndex) for a domestic building,
 *      or a fixed BARN_EAVES_M (never jittered) for a non-domestic one
 *      (issue #113's useBucket, default 'domestic' so the pre-#113 two-arg
 *      call signature is unchanged).
 * Explicit OSM data (1, 2) is never overridden by the classifier or by
 * useBucket. Height is purely visual scene furniture — this feeds geometry
 * only, never LOS (hard constraint #1) or camera/terrain collision.
 */
export function buildingHeight(
  tags: { levels?: number; height_m?: number | null; appearance?: BuildingAppearance },
  seedIndex: number,
  useBucket: BuildingUseBucket = 'domestic',
): number {
  const measured = measuredHeights(tags.appearance);
  if (measured != null) return measured.eaves_m;
  if (tags.height_m != null) return tags.height_m;
  if (tags.levels != null) return tags.levels * STOREY_M;
  const storeys = usableStoreys(tags.appearance);
  if (storeys != null) return storeys * STOREY_M;
  if (useBucket === 'non-domestic') return BARN_EAVES_M;
  const nominal = NOMINAL_LEVELS * STOREY_M;
  // +/- ~15% deterministic variation so nominal cottages aren't clones.
  const jitter = (hash01(seedIndex) * 2 - 1) * 0.15;
  return nominal * (1 + jitter);
}

export interface RidgeAxis {
  /** unit direction of the ridge (the footprint's longest-edge direction), in local x/z */
  dir: { x: number; z: number };
  /** oriented bounding box half-extent along the ridge (long) axis, metres */
  halfLength: number;
  /** oriented bounding box half-extent across the ridge (short axis), metres */
  halfSpan: number;
  /** oriented bounding box centre, local x/z */
  centre: LocalXZ;
}

/**
 * Orientation for the gabled roof: the ridge runs parallel to the
 * footprint's dominant direction, not necessarily world x/z — most real
 * OSM footprints sit at an angle, and an axis-aligned bbox would point the
 * ridge the wrong way and leave the eaves floating off the walls.
 *
 * We close the ring (last->first, matching buildWalls/buildRoof), measure
 * every edge, and take the LONGEST edge's direction as the ridge axis
 * (ties keep the first-encountered longest edge, so a grid-aligned
 * rectangle or an exact square is still fully deterministic). The ring is
 * then rotated into that (along, across) frame, its axis-aligned bounding
 * box taken THERE — this is the true oriented bounding box — and the
 * centre rotated back to local x/z. For an already grid-aligned
 * rectangular footprint whose longest edge runs along world x or z, this
 * reduces exactly to the previous axis-aligned behaviour.
 */
export function ridgeAxis(ringLocal: LocalXZ[]): RidgeAxis {
  const n = ringLocal.length;

  // Longest edge of the closed ring sets the ridge direction.
  let dir = { x: 1, z: 0 };
  let maxLenSq = -Infinity;
  for (let i = 0; i < n; i++) {
    const a = ringLocal[i];
    const b = ringLocal[(i + 1) % n];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const lenSq = dx * dx + dz * dz;
    if (lenSq > maxLenSq) {
      maxLenSq = lenSq;
      const len = Math.sqrt(lenSq);
      dir = len > 1e-9 ? { x: dx / len, z: dz / len } : { x: 1, z: 0 };
    }
  }
  // Cross-ridge unit vector (dir rotated +90 degrees).
  const perp = { x: -dir.z, z: dir.x };

  // Bounding box in the rotated (along, across) frame == the oriented bbox.
  let minAlong = Infinity;
  let maxAlong = -Infinity;
  let minAcross = Infinity;
  let maxAcross = -Infinity;
  for (const p of ringLocal) {
    const along = p.x * dir.x + p.z * dir.z;
    const across = p.x * perp.x + p.z * perp.z;
    if (along < minAlong) minAlong = along;
    if (along > maxAlong) maxAlong = along;
    if (across < minAcross) minAcross = across;
    if (across > maxAcross) maxAcross = across;
  }

  const alongCentre = (minAlong + maxAlong) / 2;
  const acrossCentre = (minAcross + maxAcross) / 2;
  // Rotate the oriented-frame centre back into local x/z.
  const centre = {
    x: alongCentre * dir.x + acrossCentre * perp.x,
    z: alongCentre * dir.z + acrossCentre * perp.z,
  };

  return {
    dir,
    halfLength: (maxAlong - minAlong) / 2,
    halfSpan: (maxAcross - minAcross) / 2,
    centre,
  };
}

export interface WallGeometry {
  /** triangle-list positions, 3 floats/vertex */
  positions: Float32Array;
  /** matching uv per vertex, 2 floats/vertex, in METRES (not normalised [0,1]) — see facadeTexture.ts's RepeatWrapping */
  uvs: Float32Array;
}

/**
 * Extrudes the CLOSED footprint perimeter (edges include the closing
 * last->first edge — buildings.json rings are stored open) into vertical
 * wall quads. Base at local y=0, top at y=eavesHeight. Each edge emits 2
 * triangles (6 verts = 18 floats). Degenerate rings (<3 points) yield empty
 * arrays — nothing to extrude.
 *
 * Also emits a matching per-vertex `uv` in metres (not normalised [0,1]):
 * u runs 0 -> edgeLength along that edge, v runs 0 (ground) -> eavesHeight
 * (top). The sandstone facade material's texture uses RepeatWrapping with
 * `repeat` scaled to 1/textureWidthM so these metre-based UVs tile the
 * texture across the wall by physical size rather than clamping to a
 * single texel (the bug this fixes — see facadeTexture.ts).
 */
export function buildWalls(ringLocal: LocalXZ[], eavesHeight: number): WallGeometry {
  if (ringLocal.length < 3) return { positions: new Float32Array(0), uvs: new Float32Array(0) };
  const positions: number[] = [];
  const uvs: number[] = [];
  const n = ringLocal.length;
  for (let i = 0; i < n; i++) {
    const a = ringLocal[i];
    const b = ringLocal[(i + 1) % n]; // wraps last->first, closing the ring
    const edgeLength = Math.hypot(b.x - a.x, b.z - a.z);

    // Quad: (a,0) (b,0) (b,h) / (a,0) (b,h) (a,h)
    positions.push(a.x, 0, a.z);
    positions.push(b.x, 0, b.z);
    positions.push(b.x, eavesHeight, b.z);

    positions.push(a.x, 0, a.z);
    positions.push(b.x, eavesHeight, b.z);
    positions.push(a.x, eavesHeight, a.z);

    // Matching uv, per edge: u from 0 to edgeLength, v from 0 to eavesHeight.
    uvs.push(0, 0);
    uvs.push(edgeLength, 0);
    uvs.push(edgeLength, eavesHeight);

    uvs.push(0, 0);
    uvs.push(edgeLength, eavesHeight);
    uvs.push(0, eavesHeight);
  }
  return { positions: new Float32Array(positions), uvs: new Float32Array(uvs) };
}

interface Point3 {
  x: number;
  y: number;
  z: number;
}

function pushTri(out: number[], a: Point3, b: Point3, c: Point3): void {
  out.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
}

/** The three roof forms this builder supports. Anything that is not exactly
 * "hipped" or "flat" (absent, "gabled", "other", or an unrecognised value)
 * resolves to the gable, so output stays regression-identical for today's
 * data (issue #104 fallback table). */
export type RoofKind = 'gabled' | 'hipped' | 'flat';

/** Normalises a raw appearance.roof_type tag to a RoofKind. Exact-match only —
 * a stray "Hipped"/"pitched"/etc. falls through to the safe gable default. */
export function roofKind(roofType?: string): RoofKind {
  if (roofType === 'hipped') return 'hipped';
  if (roofType === 'flat') return 'flat';
  return 'gabled';
}

/** Below this ridge half-length (metres) a hip is treated as collapsed to a
 * point: the two long faces are emitted as single triangles instead of
 * trapezoids, giving a clean pyramid with no zero-area slivers. */
const RIDGE_COLLAPSE_EPS = 1e-6;

/**
 * Shared pitched-roof builder for the gable and the hip — they differ only in
 * how far the ridge is inset from the ends. `ridgeHalfLength` is the half-length
 * of the ridge line measured from the bbox centre along the ridge direction:
 *   - gable: ridge runs the full long axis (ridgeHalfLength = halfLength), so
 *     the two end triangles stand vertical as gable caps;
 *   - hip:   ridge inset by halfSpan from each end (ridgeHalfLength =
 *     max(0, halfLength - halfSpan)), so those same end triangles slope inward
 *     as hip end-planes.
 * The apex height and eave corners are identical in both cases, which is why
 * the gable path (ridgeHalfLength = halfLength) reproduces the pre-#104 output
 * byte-for-byte. When the ridge collapses (near-square hip) the long faces
 * degenerate to single triangles and the result is a valid pyramid.
 */
function buildPitchedRoof(ridge: RidgeAxis, eavesHeight: number, apexHeight: number, ridgeHalfLength: number): Float32Array {
  const perp = { x: -ridge.dir.z, z: ridge.dir.x }; // cross-ridge unit vector

  const ridgeA: Point3 = {
    x: ridge.centre.x - ridge.dir.x * ridgeHalfLength,
    y: apexHeight,
    z: ridge.centre.z - ridge.dir.z * ridgeHalfLength,
  };
  const ridgeB: Point3 = {
    x: ridge.centre.x + ridge.dir.x * ridgeHalfLength,
    y: apexHeight,
    z: ridge.centre.z + ridge.dir.z * ridgeHalfLength,
  };

  const eaveCorner = (alongSign: number, perpSign: number): Point3 => ({
    x: ridge.centre.x + ridge.dir.x * alongSign * ridge.halfLength + perp.x * perpSign * ridge.halfSpan,
    y: eavesHeight,
    z: ridge.centre.z + ridge.dir.z * alongSign * ridge.halfLength + perp.z * perpSign * ridge.halfSpan,
  });

  const eaveA1 = eaveCorner(-1, 1);
  const eaveA2 = eaveCorner(-1, -1);
  const eaveB1 = eaveCorner(1, 1);
  const eaveB2 = eaveCorner(1, -1);

  const collapsed = ridgeHalfLength <= RIDGE_COLLAPSE_EPS;
  const out: number[] = [];
  // Long faces, one per side of the ridge. Trapezoids in the general case; when
  // the ridge collapses to a point the second triangle would be zero-area, so
  // it is dropped (the face is then a single triangle to the apex).
  pushTri(out, eaveA1, eaveB1, ridgeB);
  if (!collapsed) pushTri(out, eaveA1, ridgeB, ridgeA);
  pushTri(out, eaveB2, eaveA2, ridgeA);
  if (!collapsed) pushTri(out, eaveB2, ridgeA, ridgeB);
  // End faces: vertical gable caps when ridge is full length, sloped hip ends
  // when inset. Always valid triangles (they carry the collapsed pyramid too).
  pushTri(out, eaveA2, eaveA1, ridgeA);
  pushTri(out, eaveB1, eaveB2, ridgeB);

  return new Float32Array(out);
}

/** Flat cap: a single horizontal quad at eaves height over the oriented bbox
 * (same bbox the pitched roofs cover). No ridge, no pitch. */
function buildFlatCap(ridge: RidgeAxis, eavesHeight: number): Float32Array {
  const perp = { x: -ridge.dir.z, z: ridge.dir.x };
  const corner = (alongSign: number, perpSign: number): Point3 => ({
    x: ridge.centre.x + ridge.dir.x * alongSign * ridge.halfLength + perp.x * perpSign * ridge.halfSpan,
    y: eavesHeight,
    z: ridge.centre.z + ridge.dir.z * alongSign * ridge.halfLength + perp.z * perpSign * ridge.halfSpan,
  });
  const c1 = corner(-1, 1);
  const c2 = corner(-1, -1);
  const c3 = corner(1, 1);
  const c4 = corner(1, -1);

  const out: number[] = [];
  pushTri(out, c1, c3, c4);
  pushTri(out, c1, c4, c2);
  return new Float32Array(out);
}

/**
 * Roof geometry for a single lobe over its oriented bounding box (ridgeAxis),
 * selected by `roofType` (issue #104):
 *   - "gabled" / absent / "other" / unrecognised → gable: ridge along the long
 *     axis at `apexHeight = eavesHeight + tan(pitchDeg) * halfSpan`, two sloped
 *     rectangular faces plus two triangular gable-end caps.
 *   - "hipped" → four-plane hip at the same pitch/apex, ridge inset from each
 *     end by halfSpan; collapses to a valid pyramid on a near-square lobe.
 *   - "flat" → a flat cap at eaves height (no ridge, no pitch).
 * A needle-thin lobe (isSliverLobe) is always capped flat regardless of
 * `roofType`, so decomposition never raises a spindly apex over a sliver
 * (issue #114). For a non-sliver whole-footprint lobe this reproduces the
 * pre-#114 output byte-for-byte.
 */
function buildLobeRoof(lobeLocal: LocalXZ[], eavesHeight: number, pitchDeg: number, roofType?: string): Float32Array {
  const ridge = ridgeAxis(lobeLocal);
  const kind = isSliverLobe(lobeLocal) ? 'flat' : roofKind(roofType);
  if (kind === 'flat') return buildFlatCap(ridge, eavesHeight);
  const apexHeight = eavesHeight + Math.tan((pitchDeg * Math.PI) / 180) * ridge.halfSpan;
  // Hip inset: ridge length = max(0, L - W) => half-length = max(0, halfLength - halfSpan).
  const ridgeHalfLength = kind === 'hipped' ? Math.max(0, ridge.halfLength - ridge.halfSpan) : ridge.halfLength;
  return buildPitchedRoof(ridge, eavesHeight, apexHeight, ridgeHalfLength);
}

/**
 * Roof geometry over a footprint, one roof per lobe (issue #114). A
 * near-rectangular footprint stays a single lobe equal to the whole ring, so
 * this reproduces the pre-#114 single-bbox roof byte-for-byte (and keeps the
 * #104 golden gable fixture passing). A concave, multi-wing footprint
 * decomposes into >=2 rectangular-ish lobes (decomposeFootprint), each roofed
 * independently at its own ridge axis and apex, with no roof vertex more than
 * ~2 m horizontally outside the footprint — instead of one giant canopy over
 * the courtyards between wings. `roofType` (one classified type for the whole
 * building) applies to every lobe.
 *
 * Runs once per building at construction (never on the render loop) and emits
 * one concatenated triangle-list, so a multi-lobe building is still a single
 * draw call. Degenerate rings (<3 points) yield an empty array under every
 * shape.
 */
export function buildRoof(
  ringLocal: LocalXZ[],
  eavesHeight: number,
  pitchDeg = DEFAULT_ROOF_PITCH_DEG,
  roofType?: string,
): Float32Array {
  if (ringLocal.length < 3) return new Float32Array(0);
  const lobes = decomposeFootprint(ringLocal);
  // Single lobe (the near-rectangular identity path): return its roof directly,
  // byte-identical to pre-#114 output — no concat wrapping.
  if (lobes.length === 1) return buildLobeRoof(lobes[0], eavesHeight, pitchDeg, roofType);
  const parts = lobes.map((lobe) => buildLobeRoof(lobe, eavesHeight, pitchDeg, roofType));
  let total = 0;
  for (const part of parts) total += part.length;
  const out = new Float32Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/**
 * Per-vertex roof UV, derived directly from a roof position buffer (u = local
 * x, v = local y/height), in METRES like buildWalls's uvs — same
 * RepeatWrapping convention facadeTexture.ts's makeRoofTexture relies on
 * (issue #151). Deliberately kept OUTSIDE buildRoof rather than threaded
 * through its return value: buildRoof's Float32Array-of-positions signature
 * and every golden/geometry regression test that reads it (buildRoof gable/
 * hipped/flat, per-lobe, chimney fixtures) stay byte-for-byte untouched — this
 * is a pure post-hoc derivation from the SAME positions, never a shape
 * change. Height (not a wall-style per-edge run) is the natural "up the
 * slope" axis for coursing lines that read as courses climbing from eaves to
 * ridge.
 */
export function roofUVsFromPositions(positions: Float32Array): Float32Array {
  const uvs = new Float32Array((positions.length / 3) * 2);
  for (let i = 0, v = 0; i < positions.length; i += 3, v += 2) {
    uvs[v] = positions[i];
    uvs[v + 1] = positions[i + 1];
  }
  return uvs;
}

export interface ChimneyBox {
  /** local x centre */
  x: number;
  /** local z centre */
  z: number;
  /** base y, local building space (0 = ground) — sits ON the sloped roof surface at (x,z), not at the flat eaves line */
  y: number;
  width: number;
  depth: number;
  height: number;
}

/**
 * 1-2 small chimney blocks near the ends of the roof ridge, chosen
 * deterministically via hash01(seedIndex). Purely decorative massing (not a
 * surveyed feature) — illustrative proportions only. Degenerate rings
 * (<3 points) yield an empty array.
 *
 * Every chimney shares the same fixed CHIMNEY_WIDTH_M/CHIMNEY_HEIGHT_M
 * (so callers can reuse one BoxGeometry). The base `y` is the roof's own
 * surface height directly beneath the chimney's (x,z), so a fixed-height box
 * always pokes CHIMNEY_HEIGHT_M above the roof at its own position instead of
 * floating or being half-buried (issue #104). The surface depends on the roof
 * shape (`roofType`, same fallback rule as buildRoof):
 *   - gable: eaves + tan(pitch) * (halfSpan - |across|)          [unchanged]
 *   - hipped: eaves + tan(pitch) * min(halfSpan - |across|, halfLength - |along|)
 *     — the lower of the side-plane and end-plane heights, so a chimney over a
 *     sloping hip end sits on the hip, not on the (higher) notional gable line;
 *   - flat: eaves height exactly (no ridge, no pitch).
 * Chimney (x,z) placement is shape-independent (it uses the same oriented
 * bbox), so the gable path stays byte-identical when roofType is omitted.
 */
export function chimneys(
  ringLocal: LocalXZ[],
  eavesHeight: number,
  seedIndex: number,
  pitchDeg = DEFAULT_ROOF_PITCH_DEG,
  roofType?: string,
): ChimneyBox[] {
  if (ringLocal.length < 3) return [];
  // Seat chimneys on the building's largest lobe so a base always sits on real
  // roof over the footprint, never floating over a courtyard gap between wings
  // (issue #114). For a near-rectangular footprint the whole ring is the single
  // lobe, so placement is byte-identical to pre-#114 (and the #104 golden
  // chimney fixture keeps passing).
  return chimneysForLobe(largestLobe(decomposeFootprint(ringLocal)), eavesHeight, seedIndex, pitchDeg, roofType);
}

/** The lobe with the greatest footprint area; ties keep the first (deterministic). */
function largestLobe(lobes: LocalXZ[][]): LocalXZ[] {
  let best = lobes[0];
  let bestArea = polygonArea(lobes[0]);
  for (let i = 1; i < lobes.length; i++) {
    const area = polygonArea(lobes[i]);
    if (area > bestArea) {
      bestArea = area;
      best = lobes[i];
    }
  }
  return best;
}

/** Chimney placement over one lobe — the pre-#114 body, keyed to the lobe's own
 * ridge axis and (sliver-aware) roof surface so the base sits on that lobe. */
function chimneysForLobe(
  lobeLocal: LocalXZ[],
  eavesHeight: number,
  seedIndex: number,
  pitchDeg: number,
  roofType?: string,
): ChimneyBox[] {
  const ridge = ridgeAxis(lobeLocal);
  const kind = isSliverLobe(lobeLocal) ? 'flat' : roofKind(roofType);
  const count = hash01(seedIndex) < 0.5 ? 1 : 2;
  const inset = Math.min(1.5, ridge.halfLength * 0.3);
  const pitchRad = (pitchDeg * Math.PI) / 180;

  const boxes: ChimneyBox[] = [];
  for (let k = 0; k < count; k++) {
    const sign = k === 0 ? 1 : -1;
    const alongOffset = sign * (ridge.halfLength - inset);
    const acrossJitter = (hash01(seedIndex * 7 + k + 1) - 0.5) * ridge.halfSpan * 0.6;
    const x = ridge.centre.x + ridge.dir.x * alongOffset + -ridge.dir.z * acrossJitter;
    const z = ridge.centre.z + ridge.dir.z * alongOffset + ridge.dir.x * acrossJitter;
    // Roof surface height at this (along, across) offset for the chosen shape.
    const acrossRise = ridge.halfSpan - Math.abs(acrossJitter); // 0 at the long eave, halfSpan at the ridge line
    let roofHeightHere: number;
    if (kind === 'flat') {
      roofHeightHere = eavesHeight;
    } else if (kind === 'hipped') {
      const alongRise = ridge.halfLength - Math.abs(alongOffset); // 0 at the end eave, halfLength at the far end
      roofHeightHere = eavesHeight + Math.tan(pitchRad) * Math.max(0, Math.min(acrossRise, alongRise));
    } else {
      roofHeightHere = eavesHeight + Math.tan(pitchRad) * Math.max(0, acrossRise);
    }
    boxes.push({ x, z, y: roofHeightHere, width: CHIMNEY_WIDTH_M, depth: CHIMNEY_WIDTH_M, height: CHIMNEY_HEIGHT_M });
  }
  return boxes;
}

/** Vertex-average centroid of a footprint ring — a fine representative point for ground sampling on small building footprints (not a true area centroid). */
function ringCentroid(ringLocal: LocalXZ[]): LocalXZ {
  let sx = 0;
  let sz = 0;
  for (const p of ringLocal) {
    sx += p.x;
    sz += p.z;
  }
  return { x: sx / ringLocal.length, z: sz / ringLocal.length };
}

/**
 * Building-only warm ambient fill (issue #154). Patches a MeshStandardMaterial
 * once (at compile) so its shaded appearance is `albedo * (sceneAmbient +
 * warmFill)` instead of `albedo * sceneAmbient`: the scene ambient is
 * sky-tinted (cool), which made sun-shaded walls read slate-blue and swamped
 * the per-building wall tint (#150). Because the added term is proportional to
 * the fragment's own `diffuseColor` (map x vertex tint), the lift is the wall's
 * own hue — a warm stone lifts warm, a dark slate barely lifts and never washes
 * to grey. Applied to building materials only; terrain keeps its own palette
 * (terrainPalette e2e stays green). Compiled once per cached material — nothing
 * on the render loop (repo perf rule). Returns the material for chaining.
 */
function applyBuildingFill(material: MeshStandardMaterial): MeshStandardMaterial {
  const fill = buildingFillPremultiplied();
  // Color(r,g,b) floats are taken as-is (no sRGB decode), so the uniform is the
  // exact linear triple lighting.ts's shadedWallAmbient reasons about.
  const uFill = new Color(fill.r, fill.g, fill.b);
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uBuildingFill = { value: uFill };
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform vec3 uBuildingFill;\nvoid main() {')
      // After direct + indirect lighting is accumulated, add the warm,
      // albedo-proportional ambient term to the diffuse response.
      .replace(
        '#include <lights_fragment_end>',
        '#include <lights_fragment_end>\n\treflectedLight.indirectDiffuse += uBuildingFill * diffuseColor.rgb;',
      );
  };
  return material;
}

// A small fixed set of sandstone facade material variants, generated once
// and shared across every building (design doc "Materials" / plan
// "Performance") — never one texture/material per building.
const NUM_FACADE_VARIANTS = 4;
const facadeMaterialCache = new Map<number, MeshStandardMaterial>();

function sandstoneMaterial(seedIndex: number): MeshStandardMaterial {
  const variant = Math.floor(hash01(seedIndex) * NUM_FACADE_VARIANTS) % NUM_FACADE_VARIANTS;
  const cached = facadeMaterialCache.get(variant);
  if (cached) return cached;
  const texture = makeSandstoneTexture(variant);
  const material = applyBuildingFill(new MeshStandardMaterial({ map: texture, roughness: 0.85, metalness: 0.02, side: DoubleSide }));
  facadeMaterialCache.set(variant, material);
  return material;
}

// --- Street View-derived appearance (issue #103; design doc
// docs/plans/2026-07-05-streetview-building-appearance-design.md). A
// building record may carry an optional `appearance` object (produced by
// #102's pipeline stage, not a dependency of this code — every field is
// treated as untrusted/optional). This is cosmetic scene furniture only:
// absence of appearance is a normal state (not a constraint-#7 blocker), and
// nothing here has any bearing on any LOS result (hard constraint #1).
//
// `Building` (core/manifest.ts) intentionally has no `appearance` field —
// widening it here via intersection, rather than editing manifest.ts,
// avoids touching a module outside this feature's write scope while still
// type-checking every existing caller that passes a plain `Building[]`.
export interface BuildingAppearance {
  roof_type?: string;
  wall_material?: string;
  wall_color?: string;
  roof_color?: string;
  confidence?: number;
  // `storeys` is consumed by buildingHeight (issue #104): a high-confidence
  // integer 1-3 drives extrusion height where OSM has no levels/height_m.
  // building_use / window_count / door_color remain INERT here — declared only
  // so a record carrying them still type-checks and passes through untouched
  // (window/door facade detail is an unfiled follow-up; building_use and
  // door_color have no viewer consumer yet), never so they're rendered.
  building_use?: string;
  storeys?: number;
  window_count?: number;
  door_color?: string;
  // LiDAR DSM-DTM measured heights (issue #119). Consumed by buildingHeight
  // (eaves) and the roof apex (ridge) via measuredHeights(); every field is
  // optional/nullable and validated there before use. `tower` is carried for a
  // later parts batch and not rendered yet.
  heights?: {
    provenance?: string;
    source?: string;
    eaves_m?: number | null;
    ridge_m?: number | null;
    roof_shape?: string | null;
    pitch_deg?: number | null;
    tower?: { top_m?: number | null } | null;
    confidence?: number | null;
    coverage_frac?: number | null;
  } | null;
  // Per-dwelling classifier records for a multi-dwelling (terrace) footprint
  // (issue #120/#121). Untrusted/optional and only consumed on a segmented
  // terrace, where terraceSegments.reconcileDwellings() validates every field
  // and maps units onto bays by extent overlap. Absent/malformed is a normal
  // fallback (hash-derived per-bay variety), never a blocker (hard constraint #7).
  dwellings?: unknown;
  // Role-labelled parts (issue #122, roadmap §3 reserved block). Untrusted/
  // optional and validated in buildingParts.assembleParts() before any use;
  // absent across the corpus today, where a tower is instead derived from the
  // mapped-use gate + measured heights.tower. Absent/malformed is a normal
  // fallback (today's #114 massing), never a blocker (hard constraint #7).
  parts?: unknown;
  // Observed per-facade window/door layout (issue #123, roadmap §3 batch 5).
  // Untrusted/optional and validated in facadeOverlay.parseFacades() before any
  // use; when a wall matches an entry by bearing the observed openings replace
  // the #116 grid on that wall, otherwise the grid stands. Absent/malformed is a
  // normal fallback (the deterministic grid), never a blocker (hard constraint #7).
  facades?: unknown;
  // Open-front/open-sided barn classification (issue #146/#157). Untrusted/
  // optional -- the classifier's accuracy on the hand-checked sample is only
  // 6/12, so this field alone never drives rendering; buildings.ts only
  // consumes it via barnVerification.verifiedOpenBarnForms, which additionally
  // requires agreement with appearance_ground_truth.json (refs #157). An
  // unverified value renders exactly as an absent one -- today's enclosed box.
  barn_form?: string | null;
}
// `osm_building` (the raw OSM `building=` tag, issue #112) is likewise absent
// from `Building` — widened here alongside `appearance` for the same reason:
// it drives the non-domestic use-bucket derivation (issue #113) without
// requiring an edit to manifest.ts outside this feature's write scope.
export type BuildingWithAppearance = Building & { appearance?: BuildingAppearance; osm_building?: string };

export type FacadeSelection =
  | { kind: 'fallback'; variant: number }
  | { kind: 'appearance'; family: WallMaterialFamily; bucket: number };

/**
 * Resolves which facade material a building should use, purely from its
 * (optional) appearance data and seed index — no three.js, no cache lookups,
 * unit-tested directly. Mirrors the issue's fallback table:
 *  - no appearance, or an unrecognised/"other" wall_material -> today's
 *    hash-based variant (byte-identical to the pre-#103 path).
 *  - a recognised wall_material with a valid wall_color -> that family's
 *    nearest colour bucket.
 *  - a recognised wall_material with a missing/malformed wall_color -> that
 *    family's default bucket (never a guess at the actual colour).
 */
export function resolveFacadeSelection(appearance: BuildingAppearance | undefined, seedIndex: number): FacadeSelection {
  const variant = Math.floor(hash01(seedIndex) * NUM_FACADE_VARIANTS) % NUM_FACADE_VARIANTS;
  const family = appearance?.wall_material;
  if (!isWallMaterialFamily(family)) return { kind: 'fallback', variant };
  const bucket = appearance?.wall_color != null ? wallColorBucket(family, appearance.wall_color) : null;
  return { kind: 'appearance', family, bucket: bucket ?? DEFAULT_WALL_COLOR_BUCKET };
}

// --- Seeded appearance variety for unclassified buildings (issue #152; the
// 641 buildings #145's Street View pass found no usable imagery for). This is
// render-side styling only: nothing here is written to buildings.json, and a
// seed is never treated as a fabricated classification (hard constraint #7
// governs REPORTED data -- a seed is never reported as an observation, and a
// building only reaches this path when there is nothing to report).

/**
 * Deterministic small unsigned-32-bit hash of an arbitrary string (FNV-1a),
 * normalised to [0, 1). Unlike hash01 (seeded by the building's INDEX in the
 * corpus array, so it moves if buildings.json is re-fetched or reordered),
 * this is keyed by the string itself -- stable across sessions, tile
 * reloads, and any corpus reordering (issue #152's seed-stability
 * requirement). Distinct suffixes on the same id (e.g. `${id}#bucket`) give
 * independent-looking draws off the one deterministic source, the same
 * idiom hash01 callers already use with distinct numeric seed offsets.
 */
export function idHash01(id: string): number {
  let h = 0x811c9dc5; // FNV-1a 32-bit offset basis
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0x100000000;
}

/**
 * Stone-biased local vernacular wall-family weights for an unclassified
 * building (issue #152): predominantly stone with an occasional render,
 * matching the rural Northumberland dwelling stock this route passes through
 * (Ridsdale, Woodburn, Corsenside) -- deliberately never brick/timber/metal,
 * which read as suburban or agricultural rather than this area's cottages.
 * Weights sum to 1; pickVernacularFamily walks them in this order.
 */
const VERNACULAR_FAMILIES: ReadonlyArray<{ family: WallMaterialFamily; weight: number }> = [
  { family: 'stone', weight: 0.82 },
  { family: 'render', weight: 0.18 },
];

function pickVernacularFamily(u: number): WallMaterialFamily {
  let acc = 0;
  for (const { family, weight } of VERNACULAR_FAMILIES) {
    acc += weight;
    if (u < acc) return family;
  }
  return VERNACULAR_FAMILIES[VERNACULAR_FAMILIES.length - 1].family;
}

/** Deterministic per-channel offset applied to a bucket's reference tint before
 * clamping, so buildings sharing a (family, bucket) don't all render as the
 * exact same flat colour. */
const VERNACULAR_TINT_JITTER = 16;

function jitterChannel(id: string, channel: string, base: number): number {
  const j = idHash01(`${id}#${channel}`) * 2 - 1; // [-1, 1)
  return Math.max(0, Math.min(255, Math.round(base + j * VERNACULAR_TINT_JITTER)));
}

export interface SeededVernacularFacade {
  family: WallMaterialFamily;
  bucket: number;
  tint: RgbColor;
  doorTint: RgbColor;
}

/**
 * Deterministic seeded material family + tint + door colour for an
 * unclassified building (issue #152). Purely render-side styling variety,
 * seeded from the building's own OSM id string -- never written to
 * buildings.json, never a fabricated classification. `family`/`bucket` are
 * always one of the already-budgeted (family, bucket) facade keys
 * (appearanceFacadeMaterial's cache), so this never mints a new shared
 * material. clampWallTint (#150) pulls the jittered tint back inside the
 * family's plausible gamut exactly as it would a real observed wall_color,
 * so a seeded building's colour is never implausible for its family.
 * Deterministic and stable across sessions/tile reloads/corpus reordering --
 * every draw is keyed off `id` alone via idHash01, never an array index or
 * load order.
 */
export function seededVernacularFacade(id: string): SeededVernacularFacade {
  const family = pickVernacularFamily(idHash01(`${id}#family`));
  const bucket = Math.floor(idHash01(`${id}#bucket`) * WALL_COLOR_BUCKET_COUNT) % WALL_COLOR_BUCKET_COUNT;
  const base = wallBaseColor(family, bucket);
  const jittered: RgbColor = {
    r: jitterChannel(id, 'r', base.r),
    g: jitterChannel(id, 'g', base.g),
    b: jitterChannel(id, 'b', base.b),
  };
  const doorIdx = Math.floor(idHash01(`${id}#door`) * DOOR_TINT_PALETTE.length) % DOOR_TINT_PALETTE.length;
  return { family, bucket, tint: clampWallTint(family, jittered), doorTint: DOOR_TINT_PALETTE[doorIdx] };
}

/**
 * True when a building has never been classified at all (issue #152): its
 * appearance carries no `building_use` -- the field the #102 pipeline always
 * sets alongside wall_material/wall_color for any building it actually
 * processed (verified against the real corpus: every wall_material value,
 * including "other", co-occurs with a building_use; the 641 buildings with no
 * usable Street View imagery are exactly the ones with building_use absent).
 * A real classification arriving later flips this back to false automatically
 * -- there is no separate seeded/unseeded flag to update. `ruins` is excluded
 * even though it isn't in NON_DOMESTIC_OSM_BUILDING (predictable-failure-case:
 * a ruin must never read as a tidy dressed dwelling, seeded or not). Callers
 * gate this on their own domestic/non-domestic resolution -- non-domestic
 * buildings never reach this check.
 */
export function isUnclassifiedBuilding(appearance: BuildingAppearance | undefined, osmBuilding: string | undefined): boolean {
  return appearance?.building_use == null && osmBuilding !== 'ruins';
}

/**
 * Small fixed reference palette `roof_color` is quantised against — bounds
 * the roof-appearance material cache to this many entries regardless of how
 * many distinct roof_color values appear in the input data (hard <=24
 * shared-material budget, issue #103). Roof colour has no "family" axis
 * (unlike walls), so this spans a few common UK roof tones directly. Widened
 * (issue #151) from the original dark-slate/red-brown/sandy-tan trio to read
 * as more distinguishable material TYPES rather than one dark plane: a
 * cooler slate blue-grey, a more saturated weathered red-brown pantile, and a
 * genuinely lighter weathered grey carrying a slight green cast for the
 * moss/weathering variety seen in reference photos — "moss-tinged" is folded
 * into that third bucket's hue rather than spent as a 4th bucket, which would
 * push the roof axis to 4 materials (1 default + 4) and break the layer's
 * exact <=24 accounting below. Kept at 3 entries (not 4) so that adding the
 * `metal` wall family (schema delta, 2026-07-05) still holds the whole layer
 * at exactly the <=24 budget: 4 fallback wall variants + 5 families * 3 wall
 * buckets + 1 default roof + 3 roof buckets + 1 chimney material = 24.
 */
const ROOF_PALETTE: RgbColor[] = [
  { r: 0x3c, g: 0x46, b: 0x50 }, // slate blue-grey (close to today's default, cooler)
  { r: 0x7a, g: 0x40, b: 0x30 }, // weathered red-brown pantile
  { r: 0x8f, g: 0x93, b: 0x86 }, // lighter weathered grey, moss-tinged
];
export const ROOF_COLOR_BUCKET_COUNT = ROOF_PALETTE.length;

/** Quantises a validated `roof_color` to the index of its nearest ROOF_PALETTE entry, or null if the hex string is malformed. */
export function roofColorBucket(hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  return nearestPaletteIndex(rgb, ROOF_PALETTE);
}

/** The reference tint for `bucket` (out-of-range clamps), mirroring wallBaseColor's shape — exposed for callers/tests that need the actual colour a bucket resolves to, not just its index. */
export function roofBaseColor(bucket: number): RgbColor {
  return ROOF_PALETTE[Math.min(Math.max(0, bucket), ROOF_PALETTE.length - 1)];
}

export type RoofSelection = { kind: 'default' } | { kind: 'appearance'; bucket: number };

/**
 * Resolves the roof material purely from (optional) roof_color — independent
 * of wall_material's validity (the issue's fallback table treats wall and
 * roof axes independently: a building can have a valid roof_color with no
 * usable wall_material, or vice versa). Missing or malformed roof_color
 * falls back to today's shared slate roof outright — there is no roof
 * "family" default to fall back into, unlike the wall path.
 */
export function resolveRoofSelection(appearance: BuildingAppearance | undefined): RoofSelection {
  if (!appearance || !isValidHexColor(appearance.roof_color)) return { kind: 'default' };
  const bucket = roofColorBucket(appearance.roof_color);
  return bucket === null ? { kind: 'default' } : { kind: 'appearance', bucket };
}

// --- Per-building window layout + tinted door on the primary facade (issue
// #116). This is GEOMETRY, not texture: the window grid (from storeys /
// window_count) and door tint (from door_color) vary per building, so baking
// them into the shared tiled wall texture would multiply the texture/material
// cache past the <=25 budget. Instead the overlay is one BufferGeometry per
// building carrying all variation in vertex colours, drawn with the single
// shared PRIMARY_DETAIL_MATERIAL (design-decision comment on #116). Pure,
// three.js-free builders below, unit-tested in Node like facadeGrid/doorSlot.

/** Resolved per-building primary-facade detail: window grid overrides + door tint. */
export interface PrimaryFacadeDetailSpec {
  /** window rows (undefined = default row count) */
  rows?: number;
  /** target window count (undefined = default layout, 0 = no windows) */
  count?: number;
  /** door tint (nearest palette entry, or today's door when door_color is null/malformed) */
  doorTint: RgbColor;
}

/**
 * Whether (and how) a building's primary facade gets the window/door overlay
 * (issue #116), from its optional appearance and use-bucket. Per-field
 * fallbacks match the schema's per-field null semantics and the issue's
 * failure table:
 *  - non-domestic (#113) -> null; suppression wins, these fields are ignored.
 *  - no appearance, or none of storeys/window_count/door_color usable -> null;
 *    the building renders byte-identical to today (no overlay mesh).
 *  - storeys present -> that many rows; storeys null but window_count present
 *    -> a single row; both absent -> default rows.
 *  - window_count present -> ~that many windows (0 = none); absent -> default.
 *  - door_color valid -> nearest palette tint; null/malformed -> today's door.
 * An out-of-range/non-finite storeys or window_count is treated as absent for
 * that aspect (never clamped into range, never fabricated — hard constraint #7).
 */
export function resolvePrimaryFacadeDetail(
  appearance: BuildingAppearance | undefined,
  useBucket: BuildingUseBucket,
): PrimaryFacadeDetailSpec | null {
  if (useBucket === 'non-domestic') return null;
  if (!appearance) return null;

  const { storeys, window_count: windowCount, door_color: doorColor } = appearance;
  const usableRows =
    typeof storeys === 'number' && Number.isFinite(storeys) && storeys >= 1 ? Math.floor(storeys) : undefined;
  const usableCount =
    typeof windowCount === 'number' && Number.isFinite(windowCount) && windowCount >= 0 ? Math.floor(windowCount) : undefined;
  const doorBucket = typeof doorColor === 'string' ? doorColorBucket(doorColor) : null;

  if (usableRows === undefined && usableCount === undefined && doorBucket === null) return null;

  // storeys null but window_count present -> single row (failure table).
  const rows = usableRows ?? (usableCount !== undefined ? 1 : undefined);
  const doorTint = doorBucket === null ? DEFAULT_DOOR_TINT_RGB : DOOR_TINT_PALETTE[doorBucket];
  return { rows, count: usableCount, doorTint };
}

/**
 * Fallback door tint for the observed-facade overlay (#123) when the matched
 * facade reports no door (doors: []). Mirrors the #116 per-field door fallback:
 * the appearance `door_color` bucket if usable, else today's fixed door tint.
 */
function observedFallbackDoorTint(appearance: BuildingAppearance | undefined): RgbColor {
  const doorColor = appearance?.door_color;
  const bucket = typeof doorColor === 'string' ? doorColorBucket(doorColor) : null;
  return bucket === null ? DEFAULT_DOOR_TINT_RGB : DOOR_TINT_PALETTE[bucket];
}

/** The building's longest footprint edge — the stylised "primary" (photographed) facade the window_count/door detail is applied to. First-encountered longest edge wins (deterministic, matching ridgeAxis). */
export function primaryFacadeEdge(ringLocal: LocalXZ[]): { a: LocalXZ; b: LocalXZ } | null {
  const n = ringLocal.length;
  if (n < 2) return null;
  let a = ringLocal[0];
  let b = ringLocal[1 % n];
  let bestLenSq = -1;
  for (let i = 0; i < n; i++) {
    const p = ringLocal[i];
    const q = ringLocal[(i + 1) % n];
    const lenSq = (q.x - p.x) ** 2 + (q.z - p.z) ** 2;
    if (lenSq > bestLenSq) {
      bestLenSq = lenSq;
      a = p;
      b = q;
    }
  }
  return bestLenSq > 1e-12 ? { a, b } : null;
}

export interface FacadeDetailGeometry {
  /** triangle-list positions, 3 floats/vertex, in building-local space (base y=0 .. eaves) */
  positions: Float32Array;
  /** matching per-vertex colour, 3 floats/vertex in 0..1 (for a vertexColors material) */
  colors: Float32Array;
}

// Small outward offsets (metres) so the overlay sits just proud of the wall and
// layers cleanly: opaque backing hides the baked tiled windows behind it, frame
// behind glazing, door alongside the glazing plane.
const OVERLAY_PROUD_BACKING = 0.03;
const OVERLAY_PROUD_FRAME = 0.05;
const OVERLAY_PROUD_GLAZING = 0.07;
const OVERLAY_PROUD_DOOR = 0.06;

function pushColoredQuad(
  pos: number[],
  col: number[],
  p0: Point3,
  p1: Point3,
  p2: Point3,
  p3: Point3,
  c: RgbColor,
): void {
  pushTri(pos, p0, p1, p2);
  pushTri(pos, p0, p2, p3);
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;
  for (let k = 0; k < 6; k++) col.push(r, g, b);
}

/**
 * Builds the primary-facade window/door overlay (issue #116) for one building:
 * an opaque backing quad in the wall's base colour, a light-frame + dark-glazing
 * pair per `facadeGrid` window, and one tinted door via `doorSlot`, all mapped
 * onto the `edgeA`->`edgeB` wall and pushed slightly proud of it along the
 * outward normal (oriented away from `interiorPoint`). Returns triangle-list
 * positions + matching vertex colours; empty for a degenerate edge. Pure and
 * deterministic by `seed` — runs once at construction, never per frame.
 *
 * `includeDoor` (default true) draws the single door. It is set false for a
 * SECONDARY long elevation (issue #148): a dwelling gets exactly one door, on
 * its primary wall, while the other long walls get the window grid only.
 */
export function buildPrimaryFacadeDetail(
  edgeA: LocalXZ,
  edgeB: LocalXZ,
  interiorPoint: LocalXZ,
  eavesHeight: number,
  baseColor: RgbColor,
  spec: PrimaryFacadeDetailSpec,
  seed: number,
  includeDoor = true,
): FacadeDetailGeometry {
  const dx = edgeB.x - edgeA.x;
  const dz = edgeB.z - edgeA.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6 || eavesHeight <= 0) return { positions: new Float32Array(0), colors: new Float32Array(0) };

  const ax = dx / len;
  const az = dz / len;
  // Outward normal = along-wall rotated -90 degrees, flipped to point away from
  // the footprint interior so the overlay faces outward.
  let nx = -az;
  let nz = ax;
  const midX = (edgeA.x + edgeB.x) / 2;
  const midZ = (edgeA.z + edgeB.z) / 2;
  if (nx * (interiorPoint.x - midX) + nz * (interiorPoint.z - midZ) > 0) {
    nx = -nx;
    nz = -nz;
  }

  const at = (u: number, v: number, proud: number): Point3 => ({
    x: edgeA.x + ax * u + nx * proud,
    y: v,
    z: edgeA.z + az * u + nz * proud,
  });

  const pos: number[] = [];
  const col: number[] = [];

  // Backing: full facade in the wall base colour, so the per-building window
  // layout reads cleanly over the tiled wall texture behind it.
  pushColoredQuad(
    pos,
    col,
    at(0, 0, OVERLAY_PROUD_BACKING),
    at(len, 0, OVERLAY_PROUD_BACKING),
    at(len, eavesHeight, OVERLAY_PROUD_BACKING),
    at(0, eavesHeight, OVERLAY_PROUD_BACKING),
    baseColor,
  );

  for (const w of facadeGrid(len, eavesHeight, seed, { rows: spec.rows, count: spec.count })) {
    // Frame width and the sill below it are shared with the observed-facade
    // overlay (facadeOverlay.ts's frameExpand/addSill, issue #149) so a
    // building never shows two different window styles depending on which
    // overlay path drew a given wall.
    const expand = frameExpand(w.w, w.h);
    const fx0 = Math.max(0, w.x - expand);
    const fy0 = Math.max(0, w.y - expand);
    const fx1 = Math.min(len, w.x + w.w + expand);
    const fy1 = Math.min(eavesHeight, w.y + w.h + expand);
    pushColoredQuad(
      pos,
      col,
      at(fx0, fy0, OVERLAY_PROUD_FRAME),
      at(fx1, fy0, OVERLAY_PROUD_FRAME),
      at(fx1, fy1, OVERLAY_PROUD_FRAME),
      at(fx0, fy1, OVERLAY_PROUD_FRAME),
      FRAME_RGB,
    );
    pushColoredQuad(
      pos,
      col,
      at(w.x, w.y, OVERLAY_PROUD_GLAZING),
      at(w.x + w.w, w.y, OVERLAY_PROUD_GLAZING),
      at(w.x + w.w, w.y + w.h, OVERLAY_PROUD_GLAZING),
      at(w.x, w.y + w.h, OVERLAY_PROUD_GLAZING),
      GLAZING_RGB,
    );
    addSill(pos, col, at, len, fx0, fx1, fy0);
  }

  // Door: only when it seats fully within the facade bounds (narrow gable ends
  // are left without one rather than spilling past the wall). Suppressed on a
  // secondary long elevation (issue #148) so a dwelling has exactly one door.
  if (includeDoor) {
    const door = doorSlot(len, seed);
    if (door.x >= 0 && door.x + door.w <= len && door.h <= eavesHeight) {
      pushColoredQuad(
        pos,
        col,
        at(door.x, 0, OVERLAY_PROUD_DOOR),
        at(door.x + door.w, 0, OVERLAY_PROUD_DOOR),
        at(door.x + door.w, door.h, OVERLAY_PROUD_DOOR),
        at(door.x, door.h, OVERLAY_PROUD_DOOR),
        spec.doorTint,
      );
    }
  }

  return { positions: new Float32Array(pos), colors: new Float32Array(col) };
}

// --- All-elevation window/door dressing (issue #148). #116/#123 dressed only
// the primary (longest) wall, leaving every other elevation a blank box in any
// oblique / gable / rear view (the largest recognisability gap in the #145
// photo-vs-render review). These pure builders extend the same shared-material
// overlay to EVERY wall: long (eaves) walls get the window grid scaled to their
// own length + storeys, gable ends get 0-2 small upper windows, and exactly one
// door stays on the primary wall. Still one merged overlay mesh per building on
// the single PRIMARY_DETAIL_MATERIAL — no new materials, no per-building draw
// call growth (issue #17), and never touches LOS (hard constraint #1).

/** cos(45deg): an edge whose direction is within 45 degrees of the ridge is a
 * long (eaves) elevation and gets the full grid; a more perpendicular edge is a
 * gable end and gets only upper windows. */
const LONG_ELEVATION_MIN_DOT = Math.SQRT1_2;

/** Shortest elevation (m) still worth dressing: mirrors facadeGrid's own width
 * floor (2 * EDGE_MARGIN + WINDOW_W, EDGE_MARGIN = 0.6 in facadeTexture), so a
 * long wall we dress always yields at least one window and a wall narrower than
 * this is left plain (issue #148 predictable-failure-case: short/degenerate
 * edges). */
const MIN_ELEVATION_WIDTH_M = 2 * 0.6 + WINDOW_W;

/** Edge margin (m) that keeps gable upper windows off the wall corners. */
const GABLE_MARGIN_M = 0.6;
/** Horizontal gap (m) between two gable upper windows (~one window width). */
const GABLE_WINDOW_GAP_M = WINDOW_W;

/**
 * Number of small upper windows on a gable end (issue #148). A known `rows`
 * (from storeys) gives 1-2 windows only when the wall is at least two storeys
 * (a single-storey gable stays plain); a null `rows` uses a seeded 0-2 fallback
 * (the per-field-null hash pattern of bayDetail — never a guess written back to
 * data, hard constraint #7). Deterministic by `seed`.
 */
export function gableUpperCount(rows: number | undefined, seed: number): number {
  if (rows === undefined) return Math.floor(hash01(seed) * 3); // seeded 0-2 fallback
  if (rows < 2) return 0; // single-storey gable stays plain
  return hash01(seed) < 0.5 ? 1 : 2;
}

/**
 * Builds a gable-end overlay for one wall (issue #148): an opaque backing quad
 * in the wall base colour, then `upperCount` small windows (frame + glazing +
 * sill, the shared #149 window style) centred high on the wall just below the
 * eaves. Reuses the same outward-normal orientation and proud offsets as
 * buildPrimaryFacadeDetail so a gable's windows sit at the same depth as the
 * long walls'. Empty (the wall is left plain, showing the tiled texture) for a
 * degenerate/short edge, `upperCount <= 0`, or a wall too short to seat a window
 * below the eaves. Pure and deterministic (`upperCount` already carries the
 * per-elevation seeded variation; placement is centred).
 */
export function buildGableEndDetail(
  edgeA: LocalXZ,
  edgeB: LocalXZ,
  interiorPoint: LocalXZ,
  eavesHeight: number,
  baseColor: RgbColor,
  upperCount: number,
): FacadeDetailGeometry {
  const empty = { positions: new Float32Array(0), colors: new Float32Array(0) };
  const dx = edgeB.x - edgeA.x;
  const dz = edgeB.z - edgeA.z;
  const len = Math.hypot(dx, dz);
  if (len < MIN_ELEVATION_WIDTH_M || eavesHeight <= 0 || upperCount <= 0) return empty;

  const ax = dx / len;
  const az = dz / len;
  let nx = -az;
  let nz = ax;
  const midX = (edgeA.x + edgeB.x) / 2;
  const midZ = (edgeA.z + edgeB.z) / 2;
  if (nx * (interiorPoint.x - midX) + nz * (interiorPoint.z - midZ) > 0) {
    nx = -nx;
    nz = -nz;
  }
  const at = (u: number, v: number, proud: number): Point3 => ({
    x: edgeA.x + ax * u + nx * proud,
    y: v,
    z: edgeA.z + az * u + nz * proud,
  });

  // Upper band, high on the wall below the eaves. A wall too short to seat a
  // window there is left plain.
  const wy = eavesHeight - GABLE_MARGIN_M - WINDOW_H;
  if (wy < 0) return empty;

  // Fit up to `upperCount` windows, centred, one gap apart.
  const usable = len - 2 * GABLE_MARGIN_M;
  const maxFit = Math.max(1, Math.floor((usable + GABLE_WINDOW_GAP_M) / (WINDOW_W + GABLE_WINDOW_GAP_M)));
  const n = Math.min(upperCount, maxFit);
  const span = n * WINDOW_W + (n - 1) * GABLE_WINDOW_GAP_M;
  const startX = (len - span) / 2;

  const pos: number[] = [];
  const col: number[] = [];
  // Backing over the whole gable so the uppers read cleanly over the tiled wall.
  pushColoredQuad(
    pos,
    col,
    at(0, 0, OVERLAY_PROUD_BACKING),
    at(len, 0, OVERLAY_PROUD_BACKING),
    at(len, eavesHeight, OVERLAY_PROUD_BACKING),
    at(0, eavesHeight, OVERLAY_PROUD_BACKING),
    baseColor,
  );
  for (let k = 0; k < n; k++) {
    const wx = startX + k * (WINDOW_W + GABLE_WINDOW_GAP_M);
    const expand = frameExpand(WINDOW_W, WINDOW_H);
    const fx0 = Math.max(0, wx - expand);
    const fy0 = Math.max(0, wy - expand);
    const fx1 = Math.min(len, wx + WINDOW_W + expand);
    const fy1 = Math.min(eavesHeight, wy + WINDOW_H + expand);
    pushColoredQuad(
      pos,
      col,
      at(fx0, fy0, OVERLAY_PROUD_FRAME),
      at(fx1, fy0, OVERLAY_PROUD_FRAME),
      at(fx1, fy1, OVERLAY_PROUD_FRAME),
      at(fx0, fy1, OVERLAY_PROUD_FRAME),
      FRAME_RGB,
    );
    pushColoredQuad(
      pos,
      col,
      at(wx, wy, OVERLAY_PROUD_GLAZING),
      at(wx + WINDOW_W, wy, OVERLAY_PROUD_GLAZING),
      at(wx + WINDOW_W, wy + WINDOW_H, OVERLAY_PROUD_GLAZING),
      at(wx, wy + WINDOW_H, OVERLAY_PROUD_GLAZING),
      GLAZING_RGB,
    );
    addSill(pos, col, at, len, fx0, fx1, fy0);
  }
  return { positions: new Float32Array(pos), colors: new Float32Array(col) };
}

// --- Open-front/open-sided barn void-face (issue #147, gated by #157's
// ground-truth verification -- see barnVerification.ts). Deliberately no
// interior geometry (the issue's requested behaviour): a dark recessed void
// backing quad stands in for the shadowed open bay, with a few support-post
// quads proud of it, all on the shared PRIMARY_DETAIL_MATERIAL like every
// other overlay -- no new materials, no per-frame work.

/**
 * Recessed-void backing colour (issue #147 owner review): picked by the
 * owner from a 5-value labelled swatch sheet (outputs/qa/issue-147/
 * void-tone-swatches.png, near-black 0x1a1a1c through mid-grey 0x4a4a4c) --
 * dark enough to read as a shadowed interior against the cladding, without
 * the near-black original's flat-black-hole look at distance/on the shaded
 * side, and light enough that the #154 albedo-proportional ambient fill
 * still has something to lift on the shaded face.
 */
const BARN_VOID_RGB: RgbColor = { r: 0x26, g: 0x28, b: 0x2c };
/**
 * Support-post colour (issue #147 owner review): a real portal-frame barn's
 * posts are galvanized steel, not timber, so this is a metallic grey --
 * a weathered-zinc tone (not the brighter first attempt, 0xc4c6c8, which the
 * owner found too bright), picked to stay visually distinct from both
 * BARN_VOID_RGB behind it and the metal-family cladding bucket at the wall
 * ends (WALL_PALETTES.metal's galvanized tint, 0x9a9d9f).
 */
const BARN_POST_RGB: RgbColor = { r: 0x8a, g: 0x8d, b: 0x90 };
const BARN_POST_WIDTH_M = 0.18;
/** Low sill/kerb the void starts above (m) -- an open bay typically doesn't
 * run flush to grade. */
const BARN_VOID_SILL_M = 0.15;
/** Inset (m) from the wall's side edges so posts read clear of the corners. */
const BARN_VOID_MARGIN_M = 0.4;
/**
 * Portal-frame bay width (m, issue #147 owner review): a real open-sided/
 * open-front barn's support posts fall in a regular structural rhythm, not a
 * fixed count -- one post roughly every 4-6 m of span. Picked the middle of
 * that range. Post count is DERIVED from the void's span (barnPostCount
 * below), not seeded, so a longer wall gets proportionally more posts.
 */
export const BARN_BAY_WIDTH_M = 5;

/**
 * Number of equidistant support posts (ends included) for a void span of
 * `spanM` metres, given BARN_BAY_WIDTH_M-ish bays: round the span to the
 * nearest whole number of bays (minimum 1 bay / 2 posts) and return
 * bays + 1. Pure and deterministic by span alone -- two buildings with the
 * same void width get the same post count.
 */
export function barnPostCount(spanM: number): number {
  const bays = Math.max(1, Math.round(spanM / BARN_BAY_WIDTH_M));
  return bays + 1;
}

/**
 * Void-face overlay for one wall of a ground-truth-verified open-front/
 * open-sided barn (issue #147). A recessed slate-grey backing quad plus
 * equidistant support posts (barnPostCount, one every ~BARN_BAY_WIDTH_M
 * metres) -- no other variation, since there is no per-building window/door
 * data to draw for a non-domestic building. Empty for a wall too short to
 * seat the minimum inset or with no usable eaves height.
 */
export function buildBarnVoidDetail(
  edgeA: LocalXZ,
  edgeB: LocalXZ,
  interiorPoint: LocalXZ,
  eavesHeight: number,
): FacadeDetailGeometry {
  const empty = { positions: new Float32Array(0), colors: new Float32Array(0) };
  const dx = edgeB.x - edgeA.x;
  const dz = edgeB.z - edgeA.z;
  const len = Math.hypot(dx, dz);
  if (len < MIN_ELEVATION_WIDTH_M || eavesHeight <= BARN_VOID_SILL_M) return empty;

  const ax = dx / len;
  const az = dz / len;
  let nx = -az;
  let nz = ax;
  const midX = (edgeA.x + edgeB.x) / 2;
  const midZ = (edgeA.z + edgeB.z) / 2;
  if (nx * (interiorPoint.x - midX) + nz * (interiorPoint.z - midZ) > 0) {
    nx = -nx;
    nz = -nz;
  }
  const at = (u: number, v: number, proud: number): Point3 => ({
    x: edgeA.x + ax * u + nx * proud,
    y: v,
    z: edgeA.z + az * u + nz * proud,
  });

  const pos: number[] = [];
  const col: number[] = [];

  const x0 = Math.min(BARN_VOID_MARGIN_M, len / 2 - 0.01);
  const x1 = len - x0;
  pushColoredQuad(
    pos,
    col,
    at(x0, BARN_VOID_SILL_M, OVERLAY_PROUD_BACKING),
    at(x1, BARN_VOID_SILL_M, OVERLAY_PROUD_BACKING),
    at(x1, eavesHeight, OVERLAY_PROUD_BACKING),
    at(x0, eavesHeight, OVERLAY_PROUD_BACKING),
    BARN_VOID_RGB,
  );

  // Equidistant posts (barnPostCount, both ends included), full height,
  // proud of the void so they read as standing in front rather than flush
  // with the shadow.
  const postCount = barnPostCount(x1 - x0);
  for (let k = 0; k < postCount; k++) {
    const t = postCount > 1 ? k / (postCount - 1) : 0.5;
    const cx = x0 + t * (x1 - x0);
    const px0 = Math.max(0, cx - BARN_POST_WIDTH_M / 2);
    const px1 = Math.min(len, cx + BARN_POST_WIDTH_M / 2);
    pushColoredQuad(
      pos,
      col,
      at(px0, 0, OVERLAY_PROUD_FRAME),
      at(px1, 0, OVERLAY_PROUD_FRAME),
      at(px1, eavesHeight, OVERLAY_PROUD_FRAME),
      at(px0, eavesHeight, OVERLAY_PROUD_FRAME),
      BARN_POST_RGB,
    );
  }

  return { positions: new Float32Array(pos), colors: new Float32Array(col) };
}

/**
 * Window/door overlay for EVERY elevation of a footprint (issue #148), merged
 * into ONE geometry so the whole building stays a single overlay draw call. Each
 * edge is classified against the ridge: a long (eaves) wall gets the window grid
 * (scaled to its own length and `spec.rows`/storeys), a gable end gets 0-2 small
 * upper windows, and exactly one door lands on the primary (longest) wall. An
 * observed facade (issue #123) whose bearing matches an edge overrides the
 * generated rhythm on THAT edge — generation fills the walls the photo never
 * saw. `spec === null` means "no generated rhythm": only observed facades render
 * (e.g. a classified church face with no usable storeys/window_count/door_color).
 * Pure and deterministic by `seed`; empty for a degenerate ring.
 *
 * `barnForm` (issue #147, non-null only for a ground-truth-verified barn --
 * see barnVerification.ts) takes over from the window/door path entirely on
 * the walls it applies to: `open_front` -> the primary wall only, `open_sided`
 * -> every long (eaves) elevation. It is mutually exclusive with `spec` in
 * practice (non-domestic buildings never resolve a `spec`), but is checked
 * ahead of the `spec === null` short-circuit so it works even if that ever
 * changes.
 */
export function buildElevationDetails(
  ringLocal: LocalXZ[],
  eavesHeight: number,
  baseColor: RgbColor,
  spec: PrimaryFacadeDetailSpec | null,
  facades: ParsedFacade[],
  fallbackDoorTint: RgbColor,
  seed: number,
  barnForm: OpenBarnForm | null = null,
): FacadeDetailGeometry {
  const empty = { positions: new Float32Array(0), colors: new Float32Array(0) };
  const n = ringLocal.length;
  if (n < 3) return empty;

  const ridge = ridgeAxis(ringLocal);
  const interior = ringCentroid(ringLocal);

  // Primary (door) wall = the longest edge; first-encountered longest wins, the
  // same deterministic tie-break as ridgeAxis / primaryFacadeEdge (so the ridge
  // axis and the door wall are the same edge, and the primary is always "long").
  let primaryIndex = 0;
  let bestLenSq = -1;
  for (let i = 0; i < n; i++) {
    const a = ringLocal[i];
    const b = ringLocal[(i + 1) % n];
    const lenSq = (b.x - a.x) ** 2 + (b.z - a.z) ** 2;
    if (lenSq > bestLenSq) {
      bestLenSq = lenSq;
      primaryIndex = i;
    }
  }
  if (bestLenSq <= 1e-12) return empty;

  const pos: number[] = [];
  const col: number[] = [];
  const append = (g: FacadeDetailGeometry): void => {
    for (let i = 0; i < g.positions.length; i++) pos.push(g.positions[i]);
    for (let i = 0; i < g.colors.length; i++) col.push(g.colors[i]);
  };

  for (let i = 0; i < n; i++) {
    const a = ringLocal[i];
    const b = ringLocal[(i + 1) % n];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6) continue; // degenerate edge left plain

    // Per-edge seed: the primary wall keeps the base seed (byte-identical door +
    // grid to the pre-#148 single-facade output); other walls derive a distinct
    // deterministic seed so their default grids/uppers vary without new inputs.
    const edgeSeed = i === primaryIndex ? seed : seed * 101 + i * 13 + 7;

    // Observed facade override (issue #123): a committed facade whose bearing
    // matches this wall replaces the generated rhythm on this wall only.
    const bearing = facades.length > 0 ? wallBearingDeg(a, b, interior) : null;
    const matched = bearing != null ? matchFacade(bearing, facades) : null;
    if (matched) {
      append(buildObservedFacadeDetail(a, b, interior, eavesHeight, baseColor, matched, fallbackDoorTint, edgeSeed));
      continue;
    }

    if (barnForm !== null) {
      // Verified void-face treatment (issue #147): open_front applies to the
      // primary wall only, open_sided to every long (eaves) elevation. Gable
      // ends are left plain either way -- no interior geometry, no generated
      // window/door rhythm on a barn wall (spec is always null for a
      // non-domestic building, so there is nothing to fall through to).
      const dot = Math.abs((dx / len) * ridge.dir.x + (dz / len) * ridge.dir.z);
      const isLongElevation = dot >= LONG_ELEVATION_MIN_DOT;
      const applies = barnForm === 'open_front' ? i === primaryIndex : isLongElevation;
      if (applies) append(buildBarnVoidDetail(a, b, interior, eavesHeight));
      continue;
    }

    if (spec === null) continue; // observed-only building: no generated rhythm

    // An observed window_count of exactly 0 means "no generated windows
    // anywhere on this building" (issue #155) — unlike a positive count, which
    // describes the photographed wall's own rate and doesn't transfer to a
    // wall of a different length, zero isn't length-dependent and DOES
    // transfer to every other elevation (secondary long walls, gable ends).
    const noGeneratedWindows = spec.count === 0;

    const dot = Math.abs((dx / len) * ridge.dir.x + (dz / len) * ridge.dir.z);
    if (dot >= LONG_ELEVATION_MIN_DOT) {
      if (i === primaryIndex) {
        // Primary long wall: the full grid + the single door (the #116 path).
        append(buildPrimaryFacadeDetail(a, b, interior, eavesHeight, baseColor, spec, edgeSeed));
      } else if (len >= MIN_ELEVATION_WIDTH_M) {
        // Other long walls: the same grid scaled to THIS wall's length, no door
        // (one door per dwelling) and no observed window_count (that count
        // describes the photographed wall, not this one) -- except a count of
        // 0, which propagates (see noGeneratedWindows above).
        append(
          buildPrimaryFacadeDetail(
            a, b, interior, eavesHeight, baseColor,
            { rows: spec.rows, count: noGeneratedWindows ? 0 : undefined, doorTint: spec.doorTint },
            edgeSeed, false,
          ),
        );
      }
    } else if (len >= MIN_ELEVATION_WIDTH_M) {
      const upperCount = noGeneratedWindows ? 0 : gableUpperCount(spec.rows, edgeSeed);
      append(buildGableEndDetail(a, b, interior, eavesHeight, baseColor, upperCount));
    }
  }

  return { positions: new Float32Array(pos), colors: new Float32Array(col) };
}

const appearanceFacadeMaterialCache = new Map<string, MeshStandardMaterial>();

function appearanceFacadeMaterial(family: WallMaterialFamily, bucket: number): MeshStandardMaterial {
  const key = `${family}:${bucket}`;
  const cached = appearanceFacadeMaterialCache.get(key);
  if (cached) return cached;
  const texture = makeFacadeTexture(family, bucket);
  // vertexColors ON (issue #150): the texture is a NEUTRAL base (facadeTexture.ts)
  // and each building multiplies it by its own observed `wall_color` via a
  // per-vertex wall tint, so one cached material per (family, bucket) still
  // serves every building at that key — no material- or draw-call growth
  // (issue #17). Every wall drawn with this material MUST carry a colour
  // attribute (bufferMesh guarantees one), or vertexColors would render it black.
  const material = applyBuildingFill(new MeshStandardMaterial({ map: texture, vertexColors: true, roughness: 0.85, metalness: 0.02, side: DoubleSide }));
  appearanceFacadeMaterialCache.set(key, material);
  return material;
}

const appearanceRoofMaterialCache = new Map<number, MeshStandardMaterial>();

/** Reserved makeRoofTexture cache slot for the default (non-appearance) roof, kept distinct from the 0..ROOF_COLOR_BUCKET_COUNT-1 appearance buckets. */
const DEFAULT_ROOF_TEXTURE_SLOT = ROOF_COLOR_BUCKET_COUNT;

function appearanceRoofMaterial(bucket: number): MeshStandardMaterial {
  const cached = appearanceRoofMaterialCache.get(bucket);
  if (cached) return cached;
  const c = ROOF_PALETTE[bucket] ?? ROOF_PALETTE[0];
  const material = applyBuildingFill(new MeshStandardMaterial({
    color: (c.r << 16) | (c.g << 8) | c.b,
    map: makeRoofTexture(bucket), // faint coursing (issue #151); neutral texture, tinted by `color` above
    roughness: 0.7,
    metalness: 0.05,
    side: DoubleSide,
  }));
  appearanceRoofMaterialCache.set(bucket, material);
  return material;
}

// Lazily constructed (never at module-import time): makeRoofTexture calls
// document.createElement('canvas'), and some test entry points install their
// DOM polyfill only inside a beforeAll hook, which runs after imports are
// resolved — an eager `const ROOF_MATERIAL = new MeshStandardMaterial({ map:
// makeRoofTexture(...) })` at module scope would throw before any test body
// runs. Deferring construction to first use (same timing as
// appearanceRoofMaterial/sandstoneMaterial, both already lazy) avoids that.
let defaultRoofMaterialInstance: MeshStandardMaterial | undefined;
function defaultRoofMaterial(): MeshStandardMaterial {
  if (!defaultRoofMaterialInstance) {
    defaultRoofMaterialInstance = applyBuildingFill(new MeshStandardMaterial({
      color: 0x3a4148,
      map: makeRoofTexture(DEFAULT_ROOF_TEXTURE_SLOT),
      roughness: 0.7,
      metalness: 0.05,
      side: DoubleSide,
    }));
  }
  return defaultRoofMaterialInstance;
}

// One shared vertex-coloured material for every chimney mesh (issue #164):
// per-building/bay wall tints ride in the colour attribute, same pattern as
// PRIMARY_DETAIL_MATERIAL — no per-chimney material keys (issue #17 budget).
export const CHIMNEY_VERTEX_MATERIAL = applyBuildingFill(new MeshStandardMaterial({
  vertexColors: true,
  roughness: 0.8,
  metalness: 0.02,
  side: DoubleSide,
}));

// The single shared material for every primary-facade window/door overlay
// (issue #116). All per-building variation (backing colour, glazing/frame, door
// tint) rides in the geometry's vertex colours, so this one material serves
// every overlay across the whole scene — +1 to the shared-material budget
// (24 -> 25; see the design-decision comment on #116), never one per building.
export const PRIMARY_DETAIL_MATERIAL = applyBuildingFill(new MeshStandardMaterial({
  vertexColors: true,
  roughness: 0.7,
  metalness: 0.03,
  side: DoubleSide,
}));

interface BuildingEntry {
  /** the building's OSM way id (`b.id`), so a landmark asset (issue #124) can
   * claim this footprint by id and suppress the procedural render. */
  id: string;
  group: Group;
  /** local x/z of the footprint's vertex-average centroid, for ground sampling */
  centroid: LocalXZ;
  resolved: boolean;
  /** true when a landmark asset (issue #124) has taken over this footprint: the
   * procedural group is kept built (so it is the instant fallback if the asset
   * fails) but never shown while suppressed. Substitution, never double-render. */
  suppressed: boolean;
  /** tile generation this entry was last probed at (NaN = never probed); see update()'s `generation` param. */
  attemptGeneration: number;
  /** whether this group's meshes currently cast shadows (issue #128). Starts true
   * to match construction (every mesh is built with castShadow=true), so the
   * distance policy only pays the child-write cost on an actual flip. */
  casting: boolean;
}

/**
 * Draw-call guardrail default (issue #91): a resolved building group farther
 * than this from the camera is hidden on the scenery tick. At ~4km a 6m
 * cottage subtends under ~2px at 1080p/60 deg FOV — visually free to cull,
 * and keeps worst-case draw calls in range even from a summit viewpoint that
 * can see the whole AOI (issue #17 low-power hardware).
 */
export const BUILDING_VISIBILITY_DISTANCE_M = 4000;

/**
 * Shadow-caster guardrail default (issue #128): a resolved building farther than
 * this from the camera stops casting into the sun shadow map on the scenery tick
 * (Med/High only — the low tier has no shadow pass). The sun's shadow camera is
 * a static ±3,000 m ortho box (`lighting.ts`), so at 2048 px it resolves
 * ~2.9 m/texel everywhere: a dwelling's shadow is a few texels, and past ~1 km it
 * covers well under a screen pixel — visually free to drop, while the shadow pass
 * measured at 86–87 % of all draw calls with buildings contributing ~1,291 of
 * ~1,430 casters (`outputs/qa/perf/2026-07-05-post-batch-review.md`). Kept inside
 * BUILDING_VISIBILITY_DISTANCE_M so the shadow band is always a subset of the
 * drawn band — a building never casts a shadow it isn't itself drawing.
 */
export const BUILDING_SHADOW_DISTANCE_M = 1200;

function bufferMesh(
  positions: Float32Array,
  uvs: Float32Array | null,
  material: MeshStandardMaterial,
  color?: RgbColor | Float32Array,
): Mesh {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  if (uvs && uvs.length > 0) {
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  }
  // A vertexColors material (the appearance wall material, issue #150) needs a
  // colour attribute or it renders black. `color` is either a single uniform
  // per-building wall tint (single mass / parts) or a ready per-vertex buffer
  // (a merged terrace, whose bays each carry their own tint). Absent, we fall
  // back to white so the neutral texture shows unmodulated rather than black.
  if (material.vertexColors === true) {
    const vertexCount = positions.length / 3;
    let colors: Float32Array;
    if (color instanceof Float32Array) {
      colors = color;
    } else {
      const tint = color ?? { r: 0xff, g: 0xff, b: 0xff };
      colors = new Float32Array(vertexCount * 3);
      const r = tint.r / 255;
      const g = tint.g / 255;
      const b = tint.b / 255;
      for (let i = 0; i < vertexCount; i++) {
        colors[i * 3] = r;
        colors[i * 3 + 1] = g;
        colors[i * 3 + 2] = b;
      }
    }
    geometry.setAttribute('color', new BufferAttribute(colors, 3));
  }
  geometry.computeVertexNormals();
  const mesh = new Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// --- Terrace bay-segmentation rendering (issue #121, the #115 follow-on). The
// pure maths (which footprints, how many bays, the clipped bay polygons, the
// dwellings[] reconciliation) lives in terraceSegments.ts and is unit-tested in
// Node; the three.js-side helpers below turn those bays into merged-by-material
// meshes. Everything is scene furniture only (hard constraint #1): no bay ever
// touches raypath/, and no bay height is invented (steppedEaves works off the
// footprint's own base height, #6/#7).

/** Emits the 12 triangles of an axis-aligned box (centre c, half-extents h) into
 * `out`. When `colorOut` and `tint` are provided, repeats the wall tint on every
 * vertex so merged chimneys can share CHIMNEY_VERTEX_MATERIAL (issue #164). */
function pushBox(
  out: number[],
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  colorOut?: number[],
  tint?: RgbColor,
): void {
  const x0 = cx - hx;
  const x1 = cx + hx;
  const y0 = cy - hy;
  const y1 = cy + hy;
  const z0 = cz - hz;
  const z1 = cz + hz;
  const p: number[][] = [
    [x0, y0, z0],
    [x1, y0, z0],
    [x1, y1, z0],
    [x0, y1, z0],
    [x0, y0, z1],
    [x1, y0, z1],
    [x1, y1, z1],
    [x0, y1, z1],
  ];
  const quads = [
    [0, 1, 2, 3],
    [5, 4, 7, 6],
    [4, 0, 3, 7],
    [1, 5, 6, 2],
    [4, 5, 1, 0],
    [3, 2, 6, 7],
  ];
  const emit = (idx: number): void => {
    out.push(...p[idx]);
    if (colorOut && tint) {
      colorOut.push(tint.r / 255, tint.g / 255, tint.b / 255);
    }
  };
  for (const [a, b, c, d] of quads) {
    emit(a);
    emit(b);
    emit(c);
    emit(a);
    emit(c);
    emit(d);
  }
}

/** A small deterministic per-bay lightness offset (±10%) around a base wall tint
 * (issue #150 terrace composition): terrace bays read as their own dwellings
 * around the row's observed colour rather than one flat mass. Clamped to 0-255. */
function perBayTint(base: RgbColor, seed: number): RgbColor {
  const f = 0.9 + hash01(seed) * 0.2;
  const clampByte = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return { r: clampByte(base.r * f), g: clampByte(base.g * f), b: clampByte(base.b * f) };
}

/**
 * Per-bay wall material + tint within the bounded shared palette (#103), so each
 * bay reads as its own dwelling without ever minting a new material key. The
 * MATERIAL still rotates the family's (already-budgeted) colour bucket per bay,
 * keeping each bay's baked window layout distinct; a fallback building rotates
 * the sandstone variant. The visible COLOUR (issue #150) composes the building's
 * observed wall tint (`observedTint`) as the base with a small deterministic
 * per-bay lightness offset — observed-as-base + per-bay variation — so the row
 * shows its real colour while dwellings still differ; a no-appearance terrace
 * keeps today's untinted sandstone. Returned as `baseColor` so the wall vertex
 * colour and the overlay backing share one value. Deterministic per
 * (seedIndex, bayIndex).
 */
function bayWallMaterial(
  facadeSelection: FacadeSelection,
  observedTint: RgbColor | null,
  seedIndex: number,
  bayIndex: number,
): { material: MeshStandardMaterial; baseColor: RgbColor } {
  const baySeed = seedIndex * 613 + bayIndex * 17 + 3;
  if (facadeSelection.kind === 'appearance') {
    const bucket = Math.floor(hash01(baySeed) * WALL_COLOR_BUCKET_COUNT) % WALL_COLOR_BUCKET_COUNT;
    const base = observedTint ?? wallBaseColor(facadeSelection.family, bucket);
    return {
      material: appearanceFacadeMaterial(facadeSelection.family, bucket),
      baseColor: perBayTint(base, baySeed),
    };
  }
  return { material: sandstoneMaterial(baySeed), baseColor: SANDSTONE_BASE_RGB };
}

/**
 * Per-bay window/door overlay spec (issue #121). A reconciled dwelling drives
 * the bay's door tint (from its observed door_color) and window count; an
 * unmapped bay, or a per-field-null dwelling, falls back to hash-derived variety
 * for THAT field only (roadmap §2c per-field nulls). Every terrace bay gets a
 * door — that is the whole point of reading the row as separate dwellings.
 */
function bayDetail(
  assignment: BayDwelling | null,
  appearance: BuildingAppearance | undefined,
  bayIndex: number,
  seed: number,
): PrimaryFacadeDetailSpec {
  const observedDoor = assignment?.doorColor ?? null;
  let doorTint: RgbColor;
  if (observedDoor != null) {
    const bucket = doorColorBucket(observedDoor);
    doorTint = bucket === null ? DEFAULT_DOOR_TINT_RGB : DOOR_TINT_PALETTE[bucket];
  } else {
    const idx = Math.floor(hash01(seed * 131 + bayIndex * 7 + 5) * DOOR_TINT_PALETTE.length) % DOOR_TINT_PALETTE.length;
    doorTint = DOOR_TINT_PALETTE[idx];
  }
  let count: number | undefined;
  if (assignment?.windowCount != null) {
    count = assignment.windowCount;
  } else if (
    typeof appearance?.window_count === 'number' &&
    Number.isFinite(appearance.window_count) &&
    appearance.window_count >= 0
  ) {
    count = Math.floor(appearance.window_count);
  }
  return { count, doorTint };
}

/**
 * Builds one terrace footprint as N per-dwelling bays (issue #121), all merged
 * BY MATERIAL so a 19-bay row is ~7 draw calls (≤ the palette's wall buckets +
 * 1 roof + 1 chimney + 1 overlay), never ~57. Bays come from the footprint's own
 * minimum-area OBB long axis; each bay's eaves is the footprint's base height
 * stepped deterministically (steppedEaves), never a re-invented height. Returns
 * the group, or null for a degenerate footprint (no walls) — matching the
 * single-mass path's empty-wall skip. One BuildingEntry per ORIGINAL footprint
 * is kept by the caller, so the #91 cull and #6/#7 grounding are unchanged.
 */
function buildSegmentedTerrace(
  b: BuildingWithAppearance,
  seedIndex: number,
  ringLocal: LocalXZ[],
  baseEaves: number,
): Group | null {
  const bays = segmentFootprint(ringLocal);

  const resolvedFacade = resolveFacadeSelection(b.appearance, seedIndex);
  // An unclassified terrace row (issue #152) still reads as a real dwelling
  // row rather than the flat sandstone default: an id-seeded vernacular
  // family stands in for the row wherever a real classification is absent,
  // and the per-bay lightness variation below (perBayTint via
  // bayWallMaterial) composes around it exactly as it would a real observed
  // wall_color. The caller only reaches buildSegmentedTerrace for domestic
  // footprints (the !isNonDomestic gate at the call site), so no domestic
  // check is needed here -- only the classification/ruins check.
  const unclassifiedSeed =
    resolvedFacade.kind !== 'appearance' && isUnclassifiedBuilding(b.appearance, b.osm_building)
      ? seededVernacularFacade(b.id)
      : null;
  const facadeSelection: FacadeSelection = unclassifiedSeed
    ? { kind: 'appearance', family: unclassifiedSeed.family, bucket: unclassifiedSeed.bucket }
    : resolvedFacade;
  const roofSelection = resolveRoofSelection(b.appearance);
  const roofMaterial = roofSelection.kind === 'default' ? defaultRoofMaterial() : appearanceRoofMaterial(roofSelection.bucket);
  const roofType = b.appearance?.roof_type;
  const measured = measuredHeights(b.appearance);
  const pitchDeg =
    measured != null
      ? roofPitchDegForRidge(measured.eaves_m, measured.ridge_m, ridgeAxis(ringLocal).halfSpan)
      : DEFAULT_ROOF_PITCH_DEG;

  const assignments = reconcileDwellings(bays.length, b.appearance?.dwellings);

  // The whole row's wall tint (issue #150), clamped to the family gamut; per-bay
  // offsets vary around it. The real observed wall_color for a classified row,
  // the id-seeded vernacular tint for an unclassified one (issue #152); null on
  // the plain fallback path (no classification AND no seed, e.g. a ruin), where
  // bays keep today's untinted sandstone.
  const observedTint =
    facadeSelection.kind === 'appearance'
      ? (unclassifiedSeed ? unclassifiedSeed.tint : wallTintColor(facadeSelection.family, b.appearance?.wall_color))
      : null;

  const wallByMaterial = new Map<MeshStandardMaterial, { pos: number[]; uv: number[]; col: number[] }>();
  const roofPositions: number[] = [];
  const chimneyPositions: number[] = [];
  const chimneyColors: number[] = [];
  const overlayPositions: number[] = [];
  const overlayColors: number[] = [];

  bays.forEach((bay, k) => {
    if (bay.length < 3) return;
    const bayEaves = steppedEaves(baseEaves, k, seedIndex);
    const baySeed = seedIndex * 977 + k * 41 + 1;

    const { material: wallMat, baseColor } = bayWallMaterial(facadeSelection, observedTint, seedIndex, k);
    const w = buildWalls(bay, bayEaves);
    if (w.positions.length > 0) {
      let acc = wallByMaterial.get(wallMat);
      if (!acc) {
        acc = { pos: [], uv: [], col: [] };
        wallByMaterial.set(wallMat, acc);
      }
      for (let i = 0; i < w.positions.length; i++) acc.pos.push(w.positions[i]);
      for (let i = 0; i < w.uvs.length; i++) acc.uv.push(w.uvs[i]);
      // Per-vertex bay tint (issue #150): even where two bays merge into one
      // material, each keeps its own colour — no bleed across the merged mesh.
      const vertexCount = w.positions.length / 3;
      const cr = baseColor.r / 255;
      const cg = baseColor.g / 255;
      const cb = baseColor.b / 255;
      for (let v = 0; v < vertexCount; v++) acc.col.push(cr, cg, cb);
    }

    const rp = buildRoof(bay, bayEaves, pitchDeg, roofType);
    for (let i = 0; i < rp.length; i++) roofPositions.push(rp[i]);

    for (const c of chimneys(bay, bayEaves, baySeed, pitchDeg, roofType)) {
      pushBox(
        chimneyPositions,
        c.x,
        c.y + c.height / 2,
        c.z,
        c.width / 2,
        c.height / 2,
        c.depth / 2,
        chimneyColors,
        baseColor,
      );
    }

    // Dress all of the bay's elevations (issue #148): both long sides get the
    // grid, the gable ends get upper windows, and exactly one door lands on the
    // bay's primary (longest) side. Terraces carry no per-bay facades[] block,
    // so no observed override applies here.
    const spec = bayDetail(assignments[k] ?? null, b.appearance, k, baySeed);
    const overlay = buildElevationDetails(bay, bayEaves, baseColor, spec, [], spec.doorTint, baySeed);
    for (let i = 0; i < overlay.positions.length; i++) overlayPositions.push(overlay.positions[i]);
    for (let i = 0; i < overlay.colors.length; i++) overlayColors.push(overlay.colors[i]);
  });

  const group = new Group();
  group.name = `building-${b.id}`;
  group.visible = false;

  let hasWalls = false;
  for (const [mat, acc] of wallByMaterial) {
    if (acc.pos.length === 0) continue;
    group.add(bufferMesh(new Float32Array(acc.pos), new Float32Array(acc.uv), mat, new Float32Array(acc.col)));
    hasWalls = true;
  }
  if (!hasWalls) return null; // degenerate footprint: nothing to render, skip

  if (roofPositions.length > 0) {
    const roofPos = new Float32Array(roofPositions);
    group.add(bufferMesh(roofPos, roofUVsFromPositions(roofPos), roofMaterial));
  }
  if (chimneyPositions.length > 0) {
    group.add(
      bufferMesh(
        new Float32Array(chimneyPositions),
        null,
        CHIMNEY_VERTEX_MATERIAL,
        new Float32Array(chimneyColors),
      ),
    );
  }
  if (overlayPositions.length > 0) {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(overlayPositions), 3));
    geometry.setAttribute('color', new BufferAttribute(new Float32Array(overlayColors), 3));
    geometry.computeVertexNormals();
    const mesh = new Mesh(geometry, PRIMARY_DETAIL_MATERIAL);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}

/**
 * Three.js wrapper: builds every building's wall/roof/chimney geometry
 * up-front in local x/z (base y unresolved), then drapes each building onto
 * the terrain collider incrementally as tiles stream in. Mirrors
 * RouteRibbon (viewer/src/terrain/routeRibbon.ts) — construct once, add
 * `.group` to the scene, call `.update(sampleGround, budget, generation)` on
 * the same cadence as tile loading. A building whose centroid has no loaded
 * ground stays hidden rather than guessing an elevation (hard constraint
 * #6/#7).
 */
export class BuildingsLayer {
  readonly group: Group;
  private readonly entries: BuildingEntry[] = [];
  /** Synthetic generations (negative, so they never collide with real tile
   * generations) handed out when update() is called without one — mirrors
   * RoadWallsLayer/WaterLayer so every call behaves like a fresh generation. */
  private fallbackGeneration = 0;

  constructor(buildings: BuildingWithAppearance[], appearanceGroundTruth: AppearanceGroundTruth = {}) {
    this.group = new Group();
    this.group.name = 'buildings';

    // Ground-truth-verified open barns (issue #147, gated by #157): computed
    // once for the whole corpus, not per building -- see barnVerification.ts
    // for why an unverified classification must render exactly as today.
    const verifiedBarnForms = verifiedOpenBarnForms(buildings, appearanceGroundTruth);

    buildings.forEach((b, i) => {
      const ringLocal = footprintToLocal(b.footprint_bng);
      if (ringLocal.length < 3) return; // degenerate footprint: skip entirely, not rendered

      // Domestic/non-domestic use-bucket (issue #113): drives eaves default,
      // roof pitch, chimney presence, and the no-appearance facade default.
      // Never affects LOS (hard constraint #1) -- purely cosmetic massing.
      const useBucket = resolveBuildingUse(b.osm_building, b.appearance?.building_use);
      const isNonDomestic = useBucket === 'non-domestic';

      const eaves = buildingHeight(b, i, useBucket);

      // Terrace bay-segmentation (issue #121, the #115 follow-on): a long, thin,
      // residential row mapped as one OSM way is split into per-dwelling bays so
      // it reads as the row of dwellings it is on the ground, instead of one
      // monolithic 105 m mass. Domestic only, gated by the use-aware predicate,
      // and only when it makes more than one bay. Everything else (short,
      // near-square, bent/low-fill, or non-domestic) falls through to the
      // single-mass path below, byte-identical to today. One BuildingEntry per
      // ORIGINAL footprint is kept, so the #91 draw-call cull and the #6/#7
      // grounding (one sampleGround per footprint, self-healing) are unchanged.
      if (!isNonDomestic && isTerraceCandidate(b.osm_building, ringLocal) && bayCount(ringLocal) > 1) {
        const terraceGroup = buildSegmentedTerrace(b, i, ringLocal, eaves);
        if (terraceGroup) {
          this.group.add(terraceGroup);
          this.entries.push({
            id: b.id,
            group: terraceGroup,
            centroid: ringCentroid(ringLocal),
            resolved: false,
            suppressed: false,
            attemptGeneration: Number.NaN,
            casting: true,
          });
        }
        return;
      }

      const wallGeometry = buildWalls(ringLocal, eaves);
      if (wallGeometry.positions.length === 0) return;

      const buildingGroup = new Group();
      buildingGroup.name = `building-${b.id}`;
      buildingGroup.visible = false;

      const facadeSelection = resolveFacadeSelection(b.appearance, i);
      // Seeded vernacular appearance for an unclassified domestic building
      // (issue #152: the 641 no-Street-View-coverage buildings, #145). Only
      // considered once a real classification (facadeSelection.kind ===
      // 'appearance') and non-domestic have both been ruled out, so a real
      // observation always wins and this never applies to a barn/shed/ruin.
      const unclassifiedSeed =
        facadeSelection.kind !== 'appearance' && !isNonDomestic && isUnclassifiedBuilding(b.appearance, b.osm_building)
          ? seededVernacularFacade(b.id)
          : null;
      let wallMaterial: MeshStandardMaterial;
      // Per-building wall tint carried as a vertex colour (issue #150): the
      // observed wall_color, clamped to the family gamut, for an appearance
      // building; the fallback family/bucket reference tint for a no-appearance
      // non-domestic building; the id-seeded vernacular tint for an
      // unclassified domestic building (issue #152); SANDSTONE_BASE_RGB
      // otherwise (unused there — sandstoneMaterial is not a vertexColors
      // material, so bufferMesh ignores it).
      let wallTint: RgbColor;
      if (facadeSelection.kind === 'appearance') {
        wallMaterial = appearanceFacadeMaterial(facadeSelection.family, facadeSelection.bucket);
        wallTint = wallTintColor(facadeSelection.family, b.appearance?.wall_color);
      } else if (isNonDomestic) {
        // No usable appearance: a non-domestic building defaults to a
        // procedural utilitarian family rather than the domestic hash-based
        // sandstone variant. Agricultural types (barn/cowshed/.../shed) get
        // timber (vertical plank cladding); the rest get metal/corrugated,
        // with industrial specifically overriding to the galvanized bucket
        // instead of the shared rust-brown default (issue #133, #137). Both
        // reuse already-shared materials -- adds nothing to the <=24
        // shared-material budget (issue #17 / #103).
        const fam = nonDomesticFallbackFamily(b.osm_building);
        const bkt = nonDomesticFallbackBucket(b.osm_building);
        wallMaterial = appearanceFacadeMaterial(fam, bkt);
        wallTint = wallBaseColor(fam, bkt);
      } else if (unclassifiedSeed) {
        // No usable classification at all (issue #152): a deterministic
        // id-seeded vernacular family/tint instead of the flat uniform
        // sandstone default, so an unclassified building stops diluting
        // street scenes that also contain dressed buildings. Still one of
        // the already-budgeted (family, bucket) facade keys -- no new shared
        // material.
        wallMaterial = appearanceFacadeMaterial(unclassifiedSeed.family, unclassifiedSeed.bucket);
        wallTint = unclassifiedSeed.tint;
      } else {
        wallMaterial = sandstoneMaterial(i);
        wallTint = SANDSTONE_BASE_RGB;
      }
      buildingGroup.add(bufferMesh(wallGeometry.positions, wallGeometry.uvs, wallMaterial, wallTint));

      const roofSelection = resolveRoofSelection(b.appearance);
      const roofMaterial = roofSelection.kind === 'default' ? defaultRoofMaterial() : appearanceRoofMaterial(roofSelection.bucket);
      const roofType = b.appearance?.roof_type;
      // A measured ridge (#119) drives the apex: derive the pitch that lifts the
      // gable from the measured eaves to the measured ridge over the footprint's
      // cross-ridge half-span; otherwise use the nominal barn/domestic pitch.
      const measured = measuredHeights(b.appearance);
      const pitchDeg =
        measured != null
          ? roofPitchDegForRidge(measured.eaves_m, measured.ridge_m, ridgeAxis(ringLocal).halfSpan)
          : isNonDomestic
            ? BARN_ROOF_PITCH_DEG
            : DEFAULT_ROOF_PITCH_DEG;
      const roofPositions = buildRoof(ringLocal, eaves, pitchDeg, roofType);
      if (roofPositions.length > 0) {
        buildingGroup.add(bufferMesh(roofPositions, roofUVsFromPositions(roofPositions), roofMaterial));
      }

      // Non-domestic buildings get no chimneys (issue #113) -- a shed/barn
      // massing has no dwelling flue to model.
      if (!isNonDomestic) {
        const chimneyPositions: number[] = [];
        const chimneyColors: number[] = [];
        for (const c of chimneys(ringLocal, eaves, i, pitchDeg, roofType)) {
          pushBox(
            chimneyPositions,
            c.x,
            c.y + c.height / 2,
            c.z,
            c.width / 2,
            c.height / 2,
            c.depth / 2,
            chimneyColors,
            wallTint,
          );
        }
        if (chimneyPositions.length > 0) {
          const chimneyMesh = bufferMesh(
            new Float32Array(chimneyPositions),
            null,
            CHIMNEY_VERTEX_MATERIAL,
            new Float32Array(chimneyColors),
          );
          chimneyMesh.castShadow = true;
          buildingGroup.add(chimneyMesh);
        }
      }

      // Per-building window/door overlay across ALL elevations (issue #148,
      // generalising the #116/#123 single-facade overlay). Added LAST so walls
      // stay child index 0 and roof index 1 for callers that index them. Every
      // wall is dressed: long (eaves) walls get the window grid, gable ends get
      // 0-2 upper windows, exactly one door lands on the primary wall, and a
      // committed facades[] entry (issue #123) overrides the generated rhythm on
      // its matching wall. `detail === null` with no matching facade dresses
      // nothing (a plain/non-domestic building is byte-identical to before).
      // All of it is one merged mesh on the shared PRIMARY_DETAIL_MATERIAL —
      // zero new materials, no per-building draw-call growth (#103/#116/#17
      // budget). Never touches LOS (hard constraint #1).
      const facades = parseFacades(b.appearance?.facades);
      // An unclassified building also gets the seeded window rhythm the
      // all-elevations path (#148) already renders for a bare spec: rows/count
      // left undefined so facadeGrid falls back to its own default grid and
      // gableUpperCount to its own seeded 0-2 fallback, just the seeded
      // doorTint set explicitly (issue #152). A real classification
      // (resolvePrimaryFacadeDetail returning non-null) always wins first.
      const detail = resolvePrimaryFacadeDetail(b.appearance, useBucket) ?? (unclassifiedSeed ? { doorTint: unclassifiedSeed.doorTint } : null);
      // Verified open-barn void-face (issue #147): non-domestic only, and only
      // when this building's id is in the corpus-wide verified set computed
      // above (see barnVerification.ts's #157 accuracy gate). Every other barn
      // -- unclassified, enclosed, or classified-but-unverified -- gets null
      // here and renders exactly as today.
      const barnForm = isNonDomestic ? verifiedBarnForms.get(b.id) ?? null : null;
      if (detail !== null || facades.length > 0 || barnForm !== null) {
        // Overlay backing matches the wall behind it: `wallTint` already holds
        // the exact colour applied to the wall above, whichever branch chose it
        // (appearance / non-domestic fallback / seeded / sandstone base).
        const baseColor = wallTint;
        const overlay = buildElevationDetails(
          ringLocal, eaves, baseColor, detail, facades, observedFallbackDoorTint(b.appearance), i, barnForm,
        );
        if (overlay.positions.length > 0) {
          const geometry = new BufferGeometry();
          geometry.setAttribute('position', new BufferAttribute(overlay.positions, 3));
          geometry.setAttribute('color', new BufferAttribute(overlay.colors, 3));
          geometry.computeVertexNormals();
          const mesh = new Mesh(geometry, PRIMARY_DETAIL_MATERIAL);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          buildingGroup.add(mesh);
        }
      }

      // Role-labelled archetype masses (issue #122): a tower (a compact lobe
      // extruded to its measured top with a pyramidal/spire cap) and a porch (a
      // small offset mass with its own low roof). These are purely ADDITIVE — the
      // main wall/roof/chimney/overlay geometry above is untouched, so a building
      // that assembles no parts (the whole corpus of plain dwellings) renders
      // byte-identical to today's #114 output. The tower gate is conservative on
      // purpose (buildingParts.ts): only ecclesiastical/civic/education USE or an
      // explicit parts[] entry raises a tower, so the 85 vegetation-contaminated
      // heights.tower candidates never spire (#119 caveat). Reuses the building's
      // already-resolved wall/roof materials — no new material keys (#103/#116/#17
      // budget). Never touches LOS (hard constraint #1).
      const parts = assembleParts(this.partsContext(b, ringLocal, eaves, measured, i));
      for (const part of parts) {
        if (part.role === 'tower') {
          const tw = buildWalls(part.footprint, part.topM);
          if (tw.positions.length > 0) buildingGroup.add(bufferMesh(tw.positions, tw.uvs, wallMaterial, wallTint));
          const cap = buildTowerCap(part.footprint, part.topM, part.capRiseM);
          if (cap.length > 0) buildingGroup.add(bufferMesh(cap, null, roofMaterial));
        } else {
          const pm = buildPorchMass(part.footprint, part.eavesM, part.ridgeM);
          if (pm.positions.length > 0) buildingGroup.add(bufferMesh(pm.positions, pm.uvs, wallMaterial, wallTint));
          if (pm.roof.length > 0) buildingGroup.add(bufferMesh(pm.roof, null, roofMaterial));
        }
      }

      this.group.add(buildingGroup);
      this.entries.push({
        id: b.id,
        group: buildingGroup,
        centroid: ringCentroid(ringLocal),
        resolved: false,
        suppressed: false,
        attemptGeneration: Number.NaN,
        casting: true,
      });
    });
  }

  /**
   * Assembles the pure PartsContext (issue #122) from the same helpers the
   * single-mass path already computed — the #114 lobes an explicit lobe_index
   * indexes into, the primary facade a porch projects from, the measured main
   * ridge for the tower prominence check, and the provenance-ladder fallback
   * heights. `heightLadder.classified` uses the raw observed storeys as a
   * documented fallback for a null-height part; the tower's real height is the
   * measured LiDAR value (never fabricated — hard constraint #7).
   */
  private partsContext(
    b: BuildingWithAppearance,
    ringLocal: LocalXZ[],
    eaves: number,
    measured: MeasuredHeights | null,
    seed: number,
  ): PartsContext {
    const storeys = b.appearance?.storeys;
    const classified =
      typeof storeys === 'number' && Number.isFinite(storeys) && storeys >= 1 ? Math.floor(storeys) * STOREY_M : null;
    return {
      osmBuilding: b.osm_building,
      appearance: b.appearance,
      ringLocal,
      lobes: decomposeFootprint(ringLocal),
      primaryEdge: primaryFacadeEdge(ringLocal),
      interior: ringCentroid(ringLocal),
      eavesM: eaves,
      ridgeM: measured?.ridge_m ?? null,
      heightLadder: { classified, nominal: eaves },
      seed,
    };
  }

  /** Count of buildings whose base has not yet been resolved to sampled ground. */
  get unresolvedCount(): number {
    return this.entries.filter((e) => !e.resolved).length;
  }

  /**
   * Re-samples not-yet-resolved buildings' centroids against the current
   * ground sampler, up to `maxSamples` raycasts this call, and drops any
   * newly-resolved building to its ground elevation and makes it visible.
   * Resolved buildings are cached forever and never re-probed.
   *
   * Mirrors the RoadWallsLayer/WaterLayer contract: `generation` identifies
   * the current terrain tile set (TerrainCollider.generation) — a
   * still-unresolved building is attempted at most once per generation, so a
   * building whose tile never streams (e.g. permanently off-route) costs
   * nothing once the whole layer has been scanned for the current tiles.
   * Omitting `generation` retries every still-unresolved building (each call
   * behaves as a fresh generation), matching pre-#91 callers.
   *
   * Returns true if the sample budget ran out before every pending building
   * was attempted this generation — i.e. "call me again, there may be more
   * ground to find now" — mirroring RoadWallsLayer.update's return contract.
   */
  update(sampleGround: GroundSampler, maxSamples = Infinity, generation?: number): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    let samples = 0;
    for (const e of this.entries) {
      if (e.resolved || e.attemptGeneration === gen) continue;
      if (samples >= maxSamples) return true; // exhausted: work remains
      samples += 1;
      e.attemptGeneration = gen;
      const ground = sampleGround(e.centroid.x, e.centroid.z);
      if (ground === null) continue;
      e.group.position.y = ground;
      // A suppressed footprint (a landmark asset took it over, #124) stays
      // hidden but is still grounded here, so it is a ready fallback the instant
      // the asset is un-suppressed (e.g. the asset failed to load).
      e.group.visible = !e.suppressed;
      e.resolved = true;
    }
    return false;
  }

  /** Whether a procedural building group exists for this OSM way id — used by the
   * landmark loader (issue #124) to distinguish a genuine footprint from an
   * orphan asset id. */
  hasBuilding(id: string): boolean {
    return this.entries.some((e) => e.id === id);
  }

  /**
   * Landmark substitution (issue #124): mark a footprint's procedural group as
   * taken over by a committed landmark asset (`suppressed = true`) so it is never
   * drawn alongside the asset — substitution, never double-render. Un-suppressing
   * (`false`) restores the procedural building as the fallback when the asset
   * turns out to be missing/unloadable. The group is kept built either way (it is
   * still ground-resolved), so a restore is instant and needs no rebuild. Returns
   * true if a matching footprint existed.
   */
  setLandmarkSuppressed(id: string, suppressed: boolean): boolean {
    let matched = false;
    for (const e of this.entries) {
      if (e.id !== id) continue;
      matched = true;
      e.suppressed = suppressed;
      if (suppressed) {
        e.group.visible = false;
      } else if (e.resolved) {
        // Restore now; an as-yet-unresolved group is shown by update() once grounded.
        e.group.visible = true;
      }
    }
    return matched;
  }

  /**
   * Draw-call guardrail (issue #91): hides resolved building groups farther
   * than `maxDistanceM` from the camera, and re-shows them when the camera
   * comes back within range. Pure distance check — no ground sampling, so
   * it's safe (and intended) to call on every scenery tick rather than every
   * frame. A building that has never resolved stays hidden regardless of
   * distance (hard constraint #6/#7: never show an un-grounded building).
   */
  updateVisibility(cameraX: number, cameraZ: number, maxDistanceM = BUILDING_VISIBILITY_DISTANCE_M): void {
    const maxDistSq = maxDistanceM * maxDistanceM;
    for (const e of this.entries) {
      if (!e.resolved || e.suppressed) continue; // a landmark-claimed footprint stays hidden (#124)
      const dx = e.centroid.x - cameraX;
      const dz = e.centroid.z - cameraZ;
      e.group.visible = dx * dx + dz * dz <= maxDistSq;
    }
  }

  /**
   * Shadow-caster policy (issue #128): a resolved building only casts into the
   * sun shadow map when it is within `maxDistanceM` of the camera; farther
   * buildings stay drawn (visibility is a separate, wider cull) but drop out of
   * the shadow pass, which measured at 86–87 % of all draw calls on Med/High
   * (`outputs/qa/perf/2026-07-05-post-batch-review.md`).
   *
   * Like {@link updateVisibility} this is a pure distance check — no ground
   * sampling — so it runs on the scenery tick (never per frame), and a camera
   * teleport (viewpoint jump / minimap click) is picked up on the next tick with
   * no movement needed. `receiveShadow` is never touched.
   *
   * `shadowsEnabled` folds the low tier into the SAME code path rather than a
   * separate branch at the call site: the low tier has no shadow pass, so the
   * policy is a plain no-op there, leaving every mesh at its construction default
   * (harmless when nothing samples the shadow map). An un-grounded building still
   * draping is skipped so its cast state never flip-flops before it is placed
   * (hard constraint #6/#7). Cast state is memoised per building, so a settled
   * scene pays only the per-entry distance compare, writing child meshes only on
   * an actual near↔far flip.
   */
  updateShadowCasters(
    cameraX: number,
    cameraZ: number,
    shadowsEnabled: boolean,
    maxDistanceM = BUILDING_SHADOW_DISTANCE_M,
  ): void {
    if (!shadowsEnabled) return; // low tier / shadows off: no shadow pass to feed
    const maxDistSq = maxDistanceM * maxDistanceM;
    for (const e of this.entries) {
      if (!e.resolved || e.suppressed) continue; // still finding ground, or landmark-claimed (#124) and hidden
      const dx = e.centroid.x - cameraX;
      const dz = e.centroid.z - cameraZ;
      const shouldCast = dx * dx + dz * dz <= maxDistSq;
      if (shouldCast === e.casting) continue; // memoised: only write on a real flip
      e.casting = shouldCast;
      for (const mesh of e.group.children) mesh.castShadow = shouldCast;
    }
  }
}
